import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../services/api';

// ─── Design primitives ────────────────────────────────────────────────────────

const Field = ({ label, icon, error, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
      {icon && <span>{icon}</span>} {label}
    </label>
    {children}
    {hint && !error && <p className="text-slate-400 text-xs mt-1.5 pl-0.5">{hint}</p>}
    {error && (
      <p className="text-red-600 text-xs mt-1.5 font-medium flex items-center gap-1 bg-red-50 px-2 py-1.5 rounded-lg">
        ⚠️ {error}
      </p>
    )}
  </div>
);

const Input = ({ className = '', ...props }) => (
  <input
    className={`w-full px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none transition-all text-slate-800 text-sm bg-white ${className}`}
    {...props}
  />
);

const Textarea = ({ className = '', ...props }) => (
  <textarea
    rows={3}
    className={`w-full px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none transition-all text-slate-800 text-sm resize-none bg-white ${className}`}
    {...props}
  />
);

const Section = ({ icon, title, children, accent = 'cyan', badge }) => {
  const gradients = {
    cyan:    'from-cyan-500 to-blue-500',
    indigo:  'from-indigo-500 to-purple-500',
    emerald: 'from-emerald-500 to-teal-500',
    amber:   'from-amber-400 to-orange-500',
    rose:    'from-rose-500 to-pink-500',
    violet:  'from-violet-500 to-purple-600',
    slate:   'from-slate-500 to-slate-600',
    fuchsia: 'from-fuchsia-500 to-pink-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className={`bg-gradient-to-r ${gradients[accent]} px-5 py-3.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{icon}</span>
          <h3 className="text-white font-semibold text-sm tracking-wide">{title}</h3>
        </div>
        {badge && (
          <span className="bg-white/20 text-white text-xs font-semibold px-2.5 py-1 rounded-full border border-white/30">
            {badge}
          </span>
        )}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
};

const Toast = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const styles = { success: 'bg-emerald-600', error: 'bg-red-600', info: 'bg-cyan-600' };
  const icons  = { success: '✅', error: '❌', info: 'ℹ️' };
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl text-white ${styles[type]} animate-slideUp`}>
      <span>{icons[type]}</span>
      <span className="font-medium text-sm">{msg}</span>
      <button onClick={onClose} className="ml-2 opacity-75 hover:opacity-100 text-base leading-none">✕</button>
    </div>
  );
};

// ─── Download helpers ─────────────────────────────────────────────────────────

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function downloadExcel(rows, columns, filename) {
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
  const XLSX = window.XLSX;
  const wsData = [
    columns.map(c => c.header),
    ...rows.map(row => columns.map(c => {
      const v = row[c.key];
      if (c.isDate && v) return new Date(v).toLocaleString('en-GB', { timeZone: 'UTC' });
      return v ?? '';
    })),
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  // Auto column widths
  ws['!cols'] = columns.map(c => ({ wch: Math.max(c.header.length, 14) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, filename);
}

async function downloadPDF(rows, columns, title, filename) {
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Header bar
  doc.setFillColor(6, 182, 212);
  doc.rect(0, 0, 297, 18, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 10, 12);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString('en-GB')}  •  ${rows.length} records`, 297 - 10, 12, { align: 'right' });

  doc.autoTable({
    startY: 22,
    head: [columns.map(c => c.header)],
    body: rows.map(row => columns.map(c => {
      const v = row[c.key];
      if (c.isDate && v) return new Date(v).toLocaleString('en-GB', { timeZone: 'UTC' });
      if (c.truncate && v) return String(v).slice(0, 60);
      return v ?? '';
    })),
    styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
    headStyles: { fillColor: [6, 182, 212], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 252, 255] },
    margin: { left: 10, right: 10 },
  });

  doc.save(filename);
}

// ─── Download bar component ───────────────────────────────────────────────────

function DownloadBar({ rows, columns, title, filenameBase, accentClass = 'from-violet-500 to-purple-600' }) {
  const [busy, setBusy] = useState(null);

  if (!rows || rows.length === 0) return null;

  const handle = async (type) => {
    setBusy(type);
    try {
      if (type === 'excel') {
        await downloadExcel(rows, columns, `${filenameBase}.xlsx`);
      } else {
        await downloadPDF(rows, columns, title, `${filenameBase}.pdf`);
      }
    } catch (e) {
      console.error('Download error', e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-400 font-medium mr-1">Download:</span>
      <button
        onClick={() => handle('excel')}
        disabled={!!busy}
        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
          busy === 'excel'
            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-wait'
            : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300'
        }`}
      >
        {busy === 'excel'
          ? <><div className="w-3 h-3 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" /> Exporting…</>
          : <>📊 Excel</>}
      </button>
      <button
        onClick={() => handle('pdf')}
        disabled={!!busy}
        className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
          busy === 'pdf'
            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-wait'
            : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:border-rose-300'
        }`}
      >
        {busy === 'pdf'
          ? <><div className="w-3 h-3 border-2 border-rose-300 border-t-rose-600 rounded-full animate-spin" /> Exporting…</>
          : <>📄 PDF</>}
      </button>
    </div>
  );
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'auto',   label: 'Automation',  icon: '⚙️' },
  { id: 'sms',    label: 'SMS Gateway', icon: '📱' },
  { id: 'wa',     label: 'WhatsApp',    icon: '💬' },
  { id: 'logs',   label: 'Logs',        icon: '📋' },
  { id: 'manual', label: 'Manual Send', icon: '🚀' },
];

// ─── Log viewer ───────────────────────────────────────────────────────────────

// const LOG_TYPES = [
//   { value: 'reminders',          label: 'SMS Reminders',       icon: '🔔' },
//   { value: 'confirmations',      label: 'SMS Confirmations',   icon: '✅' },
//   { value: 'thankyou',           label: 'SMS Thank You',       icon: '🙏' },
//   { value: 'newclients',         label: 'SMS New Clients',     icon: '👋' },
//   { value: 'birthdays',          label: 'SMS Birthdays',       icon: '🎂' },
//   { value: 'referrals',          label: 'SMS Referrals',       icon: '🤝' },
//   { value: 'seriesexpiry',       label: 'Series Expiry',       icon: '📦' },
//   { value: 'gcexpiry',           label: 'GC Expiry',           icon: '🎁' },
//   { value: 'missyou',            label: 'Miss You',            icon: '💔' },
//   { value: 'noshow',             label: 'No Show',             icon: '🚫' },
//   { value: 'wa-reminders',       label: 'WA Reminders',        icon: '💬' },
//   { value: 'wa-confirmations',   label: 'WA Confirmations',    icon: '💬' },
//   { value: 'wa-thankyou',        label: 'WA Thank You',        icon: '💬' },
//   { value: 'wa-newclients',      label: 'WA New Clients',      icon: '💬' },
//   { value: 'wa-birthdays',       label: 'WA Birthdays',        icon: '💬' },
//   { value: 'wa-referrals',       label: 'WA Referrals',        icon: '💬' },
//   { value: 'wa-seriesexpiry',    label: 'WA Series Expiry',    icon: '💬' },
//   { value: 'wa-gcexpiry',        label: 'WA GC Expiry',        icon: '💬' },
//   { value: 'wa-missyou',         label: 'WA Miss You',         icon: '💬' },
//   { value: 'wa-noshow',          label: 'WA No Show',          icon: '💬' },
//   { value: 'promo-campaigns',    label: 'Promo Campaigns',     icon: '📣', promoView: 'campaigns' },
//   { value: 'promo-sends',        label: 'Promo SMS Send Log',  icon: '📤', promoView: 'sends'     },
//   { value: 'wa-promo-sends',     label: 'Promo WA Send Log',   icon: '📤', promoView: 'wa-sends'  },
//   { value: 'promo-tests',        label: 'Promo SMS Test Log',  icon: '🧪', promoView: 'tests'     },
//   { value: 'wa-promo-tests',     label: 'Promo WA Test Log',   icon: '🧪', promoView: 'wa-tests'  },
// ];

// ─── Report types ─────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { value: 'reminders',        label: 'SMS Reminders',     icon: '🔔' },
  { value: 'prearrival',       label: 'SMS Pre-Arrival',   icon: '⏰' },
  { value: 'wa-prearrival',    label: 'WA Pre-Arrival',    icon: '⏰' },
  { value: 'confirmations',    label: 'SMS Confirmations', icon: '✅' },
  { value: 'thankyou',         label: 'SMS Thank You',     icon: '🙏' },
  { value: 'newclients',       label: 'SMS New Clients',   icon: '👋' },
  { value: 'birthdays',        label: 'SMS Birthdays',     icon: '🎂' },
  { value: 'referrals',        label: 'SMS Referrals',     icon: '🤝' },
  { value: 'seriesexpiry',     label: 'Series Expiry',     icon: '📦' },
  { value: 'gcexpiry',         label: 'GC Expiry',         icon: '🎁' },
  { value: 'missyou',          label: 'Miss You',          icon: '💔' },
  { value: 'noshow',           label: 'No Shows',          icon: '🚫' },
  { value: 'wa-reminders',     label: 'WA Reminders',      icon: '💬' },
  { value: 'wa-confirmations', label: 'WA Confirmations',  icon: '💬' },
  { value: 'wa-thankyou',      label: 'WA Thank You',      icon: '💬' },
  { value: 'wa-newclients',    label: 'WA New Clients',    icon: '💬' },
  { value: 'wa-birthdays',     label: 'WA Birthdays',      icon: '💬' },
  { value: 'wa-referrals',     label: 'WA Referrals',      icon: '💬' },
  { value: 'wa-seriesexpiry',  label: 'WA Series Expiry',  icon: '💬' },
  { value: 'wa-gcexpiry',      label: 'WA GC Expiry',      icon: '💬' },
  { value: 'wa-missyou',       label: 'WA Miss You',       icon: '💬' },
  { value: 'wa-noshow',        label: 'WA No Shows',       icon: '💬' },
  { value: 'promotions',       label: 'Promotions',        icon: '📣', isPromo: true },
];

// ─── Promotion log sub-views ──────────────────────────────────────────────────

// function PromoLogsView({ promoView }) {
//   const [campaigns, setCampaigns]   = useState([]);
//   const [logsData, setLogsData]     = useState([]);
//   const [logsTotal, setLogsTotal]   = useState(0);
//   const [logsPage, setLogsPage]     = useState(1);
//   const [logsFilter, setLogsFilter] = useState('');
//   const [loading, setLoading]       = useState(false);
//   const LOG_LIMIT = 20;

//   const isWa = promoView === 'wa-sends' || promoView === 'wa-tests';
//   const msgField = isWa ? 'WhatsAppMessage' : 'SMSMessage';

//   const fetchCampaigns = useCallback(async () => {
//     setLoading(true);
//     try {
//       const r = await api.get('/messaging/promotions/campaigns');
//       setCampaigns(r.data || []);
//     } catch {
//       setCampaigns([]);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   const fetchLogs = useCallback(async () => {
//     setLoading(true);
//     try {
//       let endpoint;
//       if (promoView === 'tests') {
//         endpoint = `/messaging/promotions/test-logs?page=${logsPage}&limit=${LOG_LIMIT}`;
//       } else if (promoView === 'wa-tests') {
//         endpoint = `/messaging/promotions/wa-test-logs?page=${logsPage}&limit=${LOG_LIMIT}`;
//       } else if (promoView === 'wa-sends') {
//         endpoint = `/messaging/promotions/wa-logs?page=${logsPage}&limit=${LOG_LIMIT}${logsFilter ? `&campaign=${encodeURIComponent(logsFilter)}` : ''}`;
//       } else {
//         endpoint = `/messaging/promotions/logs?page=${logsPage}&limit=${LOG_LIMIT}${logsFilter ? `&campaign=${encodeURIComponent(logsFilter)}` : ''}`;
//       }
//       const r = await api.get(endpoint);
//       setLogsData(r.data.data || []);
//       setLogsTotal(r.data.total || 0);
//     } catch {
//       setLogsData([]);
//       setLogsTotal(0);
//     } finally {
//       setLoading(false);
//     }
//   }, [promoView, logsPage, logsFilter]);

//   useEffect(() => {
//     setLogsPage(1);
//     setLogsFilter('');
//   }, [promoView]);

//   useEffect(() => {
//     if (promoView === 'campaigns') fetchCampaigns();
//     else fetchLogs();
//   }, [promoView, logsPage, logsFilter, fetchCampaigns, fetchLogs]);

//   const logPages = Math.ceil(logsTotal / LOG_LIMIT);

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-40">
//         <div className="w-7 h-7 border-4 border-fuchsia-200 border-t-fuchsia-500 rounded-full animate-spin" />
//       </div>
//     );
//   }

//   if (promoView === 'campaigns') {
//     return campaigns.length === 0 ? (
//       <div className="text-center py-16 text-slate-400">
//         <div className="text-4xl mb-3">📭</div>
//         <p className="font-medium text-sm">No campaigns sent yet</p>
//         <p className="text-xs mt-1">Compose and send your first campaign from the Automation tab.</p>
//       </div>
//     ) : (
//       <div className="overflow-x-auto">
//         <table className="w-full text-xs">
//           <thead>
//             <tr className="bg-slate-50 border-b border-slate-200">
//               <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Campaign</th>
//               <th className="px-4 py-3 text-right font-semibold text-slate-500 uppercase tracking-wide">SMS Sent</th>
//               <th className="px-4 py-3 text-right font-semibold text-slate-500 uppercase tracking-wide">WA Sent</th>
//               <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">First Sent</th>
//               <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Last Sent</th>
//             </tr>
//           </thead>
//           <tbody>
//             {campaigns.map((c, i) => (
//               <tr key={i} className={`border-b border-slate-100 hover:bg-fuchsia-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
//                 <td className="px-4 py-3 font-semibold text-slate-800">{c.campaignName}</td>
//                 <td className="px-4 py-3 text-right">
//                   <span className="bg-fuchsia-100 text-fuchsia-700 font-bold px-2.5 py-0.5 rounded-full">{(c.smsSent || 0).toLocaleString()}</span>
//                 </td>
//                 <td className="px-4 py-3 text-right">
//                   <span className="bg-emerald-100 text-emerald-700 font-bold px-2.5 py-0.5 rounded-full">{(c.waSent || 0).toLocaleString()}</span>
//                 </td>
//                 <td className="px-4 py-3 text-slate-500">{new Date(c.firstSent).toLocaleDateString()}</td>
//                 <td className="px-4 py-3 text-slate-500">{new Date(c.lastSent).toLocaleDateString()}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     );
//   }

//   if (promoView === 'sends' || promoView === 'wa-sends') {
//     return (
//       <div>
//         <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
//           <input
//             placeholder="Filter by campaign name…"
//             value={logsFilter}
//             onChange={e => setLogsFilter(e.target.value)}
//             className="flex-1 min-w-40 px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:border-fuchsia-300 outline-none"
//           />
//           {logsFilter && (
//             <button onClick={() => setLogsFilter('')} className="text-xs text-slate-400 hover:text-slate-600 px-2">× Clear</button>
//           )}
//           <span className="text-xs text-slate-400 ml-auto">{logsTotal.toLocaleString()} records</span>
//         </div>
//         {logsData.length === 0 ? (
//           <div className="text-center py-12 text-slate-400">
//             <div className="text-3xl mb-2">📭</div>
//             <p className="font-medium text-sm">No records found</p>
//           </div>
//         ) : (
//           <div className="overflow-x-auto">
//             <table className="w-full text-xs">
//               <thead>
//                 <tr className="bg-slate-50 border-b border-slate-200">
//                   <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Campaign</th>
//                   <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Sent At</th>
//                   <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
//                   <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Status</th>
//                   <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Message</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {logsData.map((row, i) => (
//                   <tr key={i} className={`border-b border-slate-100 hover:bg-fuchsia-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
//                     <td className="px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">{row.CName}</td>
//                     <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(row.SentDateTime).toLocaleString()}</td>
//                     <td className="px-4 py-3 text-slate-600 font-mono whitespace-nowrap">{row.MobileNo}</td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${String(row.Status).toLowerCase().includes('success') || String(row.Status).match(/^0/) ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
//                         {String(row.Status).slice(0, 30)}
//                       </span>
//                     </td>
//                     <td className="px-4 py-3 text-slate-500 max-w-xs">
//                       <span className="truncate block" title={row[msgField]}>{String(row[msgField] || '').slice(0, 60)}…</span>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//         {logPages > 1 && (
//           <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
//             <button disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}
//               className="px-3 py-1 text-xs font-semibold bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition">‹ Prev</button>
//             <span className="text-xs text-slate-400 font-medium">{logsPage} / {logPages}</span>
//             <button disabled={logsPage >= logPages} onClick={() => setLogsPage(p => p + 1)}
//               className="px-3 py-1 text-xs font-semibold bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition">Next ›</button>
//           </div>
//         )}
//       </div>
//     );
//   }

//   // Test log (SMS or WA)
//   return (
//     <div>
//       {logsData.length === 0 ? (
//         <div className="text-center py-12 text-slate-400">
//           <div className="text-3xl mb-2">🧪</div>
//           <p className="font-medium text-sm">No test messages sent yet</p>
//         </div>
//       ) : (
//         <div className="overflow-x-auto">
//           <table className="w-full text-xs">
//             <thead>
//               <tr className="bg-slate-50 border-b border-slate-200">
//                 <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Sent At</th>
//                 <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
//                 <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Status</th>
//                 <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Message</th>
//               </tr>
//             </thead>
//             <tbody>
//               {logsData.map((row, i) => (
//                 <tr key={i} className={`border-b border-slate-100 hover:bg-fuchsia-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
//                   <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(row.SentDateTime).toLocaleString()}</td>
//                   <td className="px-4 py-3 text-slate-600 font-mono whitespace-nowrap">{row.MobileNo}</td>
//                   <td className="px-4 py-3 whitespace-nowrap">
//                     <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${String(row.Status).toLowerCase().includes('success') || String(row.Status).match(/^0/) ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
//                       {String(row.Status).slice(0, 30)}
//                     </span>
//                   </td>
//                   <td className="px-4 py-3 text-slate-500 max-w-xs">
//                     <span className="truncate block" title={row[msgField]}>{String(row[msgField] || '').slice(0, 60)}…</span>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}
//       {logPages > 1 && (
//         <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
//           <button disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}
//             className="px-3 py-1 text-xs font-semibold bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition">‹ Prev</button>
//           <span className="text-xs text-slate-400 font-medium">{logsPage} / {logPages}</span>
//           <button disabled={logsPage >= logPages} onClick={() => setLogsPage(p => p + 1)}
//             className="px-3 py-1 text-xs font-semibold bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition">Next ›</button>
//         </div>
//       )}
//     </div>
//   );
// }

// ─── Reports view ─────────────────────────────────────────────────────────────

// Columns used for report downloads — #, FirstName, MobileNo, SentDateTime, Status, Message
function buildReportDownloadColumns(data) {
  if (!data || data.length === 0) return [];
  const sample = data[0];
  const cols = [
    { key: '_idx',        header: '#' },
    sample.FirstName  !== undefined && { key: 'FirstName',    header: 'First Name' },
    sample.LastName   !== undefined && { key: 'LastName',     header: 'Last Name' },
    sample.MobileNo   !== undefined && { key: 'MobileNo',     header: 'Mobile No' },
    sample.SentDateTime !== undefined && { key: 'SentDateTime', header: 'Sent At', isDate: true },
    sample.Status     !== undefined && { key: 'Status',       header: 'Status' },
    sample.Method     !== undefined && { key: 'Method',       header: 'Method' },
    sample.Charge     !== undefined && { key: 'Charge',       header: 'Charge' },
    // Message field — detect which key
    (['SMSMessage', 'WhatsAppMessage', 'Message'].find(k => sample[k] !== undefined)) &&
      { key: ['SMSMessage', 'WhatsAppMessage', 'Message'].find(k => sample[k] !== undefined), header: 'Message'},
  ].filter(Boolean);
  return cols;
}

function ReportsView() {
  const [reportType, setReportType] = useState('reminders');
  const todayStr = new Date().toISOString().slice(0, 10);
const [fromDate, setFromDate] = useState(todayStr);
const [toDate, setToDate]     = useState(todayStr);
  const [campaigns, setCampaigns]   = useState([]);
  const [campaign, setCampaign]     = useState('');
  const [data, setData]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [searched, setSearched]     = useState(false);
  const [promoSummary, setPromoSummary] = useState(null);

  const selected = REPORT_TYPES.find(t => t.value === reportType);
  const isPromo  = !!selected?.isPromo;

  useEffect(() => {
    if (!isPromo) { setCampaigns([]); setCampaign(''); return; }
    api.get('/messaging/reports/promotions/campaigns')
      .then(r => {
        const list = r.data || [];
        setCampaigns(list);
        if (list.length > 0) setCampaign(list[0].campaignName);
      })
      .catch(() => setCampaigns([]));
  }, [isPromo]);

  useEffect(() => {
    setData([]);
    setTotal(0);
    setError(null);
    setSearched(false);
    setPromoSummary(null);
  }, [reportType]);

  const runReport = async () => {
    setLoading(true);
    setError(null);
    setData([]);
    setTotal(0);
    setSearched(true);
    try {
      let url;
      if (isPromo) {
        if (!campaign) { setError('Please select a campaign.'); setLoading(false); return; }
        url = `/messaging/reports/promotions/${encodeURIComponent(campaign)}`;
      } else {
        if (!fromDate || !toDate) { setError('Please select both From and To dates.'); setLoading(false); return; }
        if (fromDate > toDate)    { setError('To date cannot be earlier than From date.'); setLoading(false); return; }
        url = `/messaging/reports/${reportType}?from=${fromDate}&to=${toDate}`;
      }
      const r = await api.get(url);
      setData(r.data.rows || []);
      if (isPromo) {
  const rows = r.data.rows || [];
  const isSuccess = s => String(s || '').toLowerCase().includes('success') || String(s || '').match(/^0/);
  setPromoSummary({
    smsSent:  r.data.smsSent  || 0,
    waSent:   r.data.waSent   || 0,
    errors:   rows.filter(row => !isSuccess(row.Status)).length,
    total:    rows.length,
  });
} else {
  setPromoSummary(null);
}
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const cols = data.length > 0
    ? Object.keys(data[0]).filter(k => k !== 'Id' && k !== 'SMSMessage' && k !== 'WhatsAppMessage' && k !== 'Message')
    : [];
  const msgKey = data[0]
    ? (['SMSMessage', 'WhatsAppMessage', 'Message'].find(k => data[0][k] !== undefined) || null)
    : null;

  // Numbered rows for download
  const downloadRows = data.map((r, i) => ({ ...r, _idx: i + 1 }));
  const downloadCols = buildReportDownloadColumns(downloadRows);
  const reportLabel  = selected?.label || reportType;
  const dateRange    = isPromo ? campaign : (fromDate && toDate ? `${fromDate}_to_${toDate}` : '');
  const filenameBase = `${reportLabel.replace(/\s+/g, '_')}_${dateRange}`;
  const pdfTitle     = `${reportLabel}${dateRange ? ` — ${dateRange}` : ''}`;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-40">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Report Type</label>
            <select
              value={reportType}
              onChange={e => setReportType(e.target.value)}
              className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm bg-white"
            >
              {REPORT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
              ))}
            </select>
          </div>
          {isPromo && (
            <div className="flex-1 min-w-40">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Campaign</label>
              {campaigns.length === 0 ? (
                <div className="px-3.5 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-400 bg-slate-50">No campaigns found</div>
              ) : (
                <select
                  value={campaign}
                  onChange={e => setCampaign(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm bg-white"
                >
                  {campaigns.map(c => <option key={c.campaignName} value={c.campaignName}>{c.campaignName}</option>)}
                </select>
              )}
            </div>
          )}
          {!isPromo && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">From</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  className="px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">To</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  className="px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm" />
              </div>
            </>
          )}
          <button
            onClick={runReport}
            disabled={loading}
            className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all shadow ${
              loading ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:shadow-md'
            }`}
          >
            {loading
              ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Loading…</>
              : <>🔍 Run Report</>}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-base">{selected?.icon}</span>
            <span className="text-sm font-semibold text-slate-700">{selected?.label}</span>
            {searched && !loading && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                {total.toLocaleString()} row{total !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {/* ── Download bar ── */}
          {searched && !loading && data.length > 0 && (
            <DownloadBar
              rows={downloadRows}
              columns={downloadCols}
              title={pdfTitle}
              filenameBase={filenameBase}
            />
          )}
          
        </div>
        {isPromo && promoSummary && searched && !loading && (
        <div className="px-4 py-3 border-b border-fuchsia-100 bg-fuchsia-50/40">
          <div className="flex flex-wrap gap-2">
            <span className="flex items-center gap-1.5 bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              📱 SMS: {promoSummary.smsSent.toLocaleString()}
            </span>
            <span className="flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              💬 WA: {promoSummary.waSent.toLocaleString()}
            </span>
            <span className="flex items-center gap-1.5 bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1.5 rounded-full">
              ✅ Total: {promoSummary.total.toLocaleString()}
            </span>
            {promoSummary.errors > 0 && (
              <span className="flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                ✗ Errors: {promoSummary.errors.toLocaleString()}
              </span>
            )}
            <span className="flex items-center gap-1.5 bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              {promoSummary.total > 0
                ? Math.round(((promoSummary.total - promoSummary.errors) / promoSummary.total) * 100)
                : 0}% success
            </span>
          </div>
        </div>
      )}
        {error && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-100 text-xs text-red-700 font-medium flex items-center gap-2">
            ⚠️ {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-7 h-7 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
          </div>
        ) : !searched ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-4xl mb-3">📊</div>
            <p className="font-medium text-sm">Select a report type and click Run Report</p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-4xl mb-3">📭</div>
            <p className="font-medium text-sm">No records found for this range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">#</th>
                  {cols.map(c => (
                    <th key={c} className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{c}</th>
                  ))}
                  {msgKey && <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Message</th>}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className={`border-b border-slate-100 hover:bg-violet-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                    <td className="px-4 py-3 text-slate-400 font-mono whitespace-nowrap">{i + 1}</td>
                    {cols.map(c => (
                      <td key={c} className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        {c === 'Status' ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            String(row[c]).toLowerCase().includes('success') || String(row[c]).match(/^0/)
                              ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>{String(row[c]).slice(0, 30)}</span>
                        ) : c === 'Channel' ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row[c] === 'WhatsApp' ? 'bg-emerald-100 text-emerald-700' : 'bg-cyan-100 text-cyan-700'}`}>
                            {row[c]}
                          </span>
                        ) : ['SentDateTime'].includes(c) ? (
                          row[c]
                            ? new Date(row[c]).toLocaleString('en-GB', { timeZone: 'UTC' })
                            : '—'
                        ) : (
                          String(row[c] ?? '').slice(0, 40)
                        )}
                      </td>
                    ))}
                    {msgKey && (
                      <td className="px-4 py-3 text-slate-600 max-w-xs">
                        <span className="truncate block" title={row[msgKey]}>{String(row[msgKey] ?? '').slice(0, 60)}…</span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Search by mobile ─────────────────────────────────────────────────────────

// Download columns for search-by-mobile: show employee/client name + all relevant fields
const MOBILE_SEARCH_DOWNLOAD_COLS = [
  { key: '_idx',        header: '#' },
  { key: 'FirstName',   header: 'First Name' },
  { key: 'LastName',    header: 'Last Name' },
  { key: 'MobileNo',    header: 'Mobile No' },
  { key: 'Type',        header: 'Type' },
  { key: 'Channel',     header: 'Channel' },
  { key: 'Campaign',    header: 'Campaign' },
  { key: 'SentDateTime',header: 'Sent At', isDate: true },
  { key: 'Status',      header: 'Status' },
  { key: 'Method',      header: 'Method' },
  { key: 'Charge',      header: 'Charge' },
  { key: 'Message',     header: 'Message'},
];

function SearchByMobileView() {
  const [mobile, setMobile]     = useState('');
  const todayStr = new Date().toISOString().slice(0, 10);
const [fromDate, setFromDate] = useState(todayStr);
const [toDate, setToDate]     = useState(todayStr);
  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [searched, setSearched] = useState(false);

  const TYPE_COLOURS = {
    'Reminders':     'bg-cyan-100 text-cyan-700',
    'Confirmations': 'bg-blue-100 text-blue-700',
    'Thank You':     'bg-indigo-100 text-indigo-700',
    'New Clients':   'bg-violet-100 text-violet-700',
    'Birthdays':     'bg-rose-100 text-rose-700',
    'Referrals':     'bg-amber-100 text-amber-700',
    'Referred To':   'bg-orange-100 text-orange-700',
    'Promotions':    'bg-fuchsia-100 text-fuchsia-700',
    'Series Expiry': 'bg-teal-100 text-teal-700',
    'GC Expiry':     'bg-emerald-100 text-emerald-700',
    'Miss You':      'bg-pink-100 text-pink-700',
    'No Show':       'bg-red-100 text-red-700',
  };

  const doSearch = async () => {
    const mob = mobile.trim();
    if (!mob || !/^\d+$/.test(mob)) { setError('Please enter a valid numeric mobile number.'); return; }
    if (fromDate && toDate && fromDate > toDate) { setError('To date cannot be earlier than From date.'); return; }
    setLoading(true);
    setError(null);
    setData([]);
    setTotal(0);
    setSearched(true);
    try {
      const params = new URLSearchParams({ mobile: mob });
      if (fromDate) params.set('from', fromDate);
      if (toDate)   params.set('to', toDate);
      const r = await api.get(`/messaging/reports/search?${params}`);
      setData(r.data.rows  || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Prepare download rows — normalise Message field, add index
  const downloadRows = data.map((row, i) => ({
    ...row,
    _idx: i + 1,
    Message: row.Message || row.SMSMessage || row.WhatsAppMessage || '',
  }));

  const mobileDateSuffix = [fromDate, toDate].filter(Boolean).join('_to_') || 'all';
  const filenameBase = `Mobile_${mobile || 'search'}_${mobileDateSuffix}`;
  const pdfTitle = `Message History — ${mobile}${fromDate ? ` (${fromDate} to ${toDate || 'now'})` : ''}`;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">📞 Mobile Number</label>
            <input
              type="tel"
              placeholder="Enter mobile number (digits only)"
              value={mobile}
              onChange={e => { setMobile(e.target.value); setError(null); }}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-slate-800 text-sm"
            />
          </div>
          {/* ── Date range ── */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={e => { setFromDate(e.target.value); setError(null); }}
              className="px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">To</label>
            <input
              type="date"
              value={toDate}
              onChange={e => { setToDate(e.target.value); setError(null); }}
              className="px-3.5 py-2.5 border border-slate-200 rounded-lg focus:border-violet-400 outline-none text-slate-800 text-sm"
            />
          </div>
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate(''); }}
              className="px-3 py-2.5 text-xs font-medium text-slate-400 hover:text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition whitespace-nowrap"
              title="Clear date filter"
            >
              × Clear dates
            </button>
          )}
          <button
            onClick={doSearch}
            disabled={loading}
            className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all shadow ${
              loading ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:shadow-md'
            }`}
          >
            {loading
              ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Searching…</>
              : <>🔍 Search</>}
          </button>
        </div>
        {(fromDate || toDate) && (
          <p className="mt-2 text-xs text-violet-600 font-medium flex items-center gap-1.5 bg-violet-50 px-3 py-1.5 rounded-lg border border-violet-200">
            📅 Date filter active:{' '}
            {fromDate && toDate ? `${fromDate} → ${toDate}` : fromDate ? `From ${fromDate}` : `Up to ${toDate}`}
          </p>
        )}
        {error && (
          <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1 bg-red-50 px-2 py-1.5 rounded-lg">⚠️ {error}</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">Results</span>
            {searched && !loading && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                {total.toLocaleString()} record{total !== 1 ? 's' : ''} across all channels
              </span>
            )}
          </div>
          {/* ── Download bar ── */}
          {searched && !loading && data.length > 0 && (
            <DownloadBar
              rows={downloadRows}
              columns={MOBILE_SEARCH_DOWNLOAD_COLS}
              title={pdfTitle}
              filenameBase={filenameBase}
            />
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-7 h-7 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
          </div>
        ) : !searched ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-4xl mb-3">🔎</div>
            <p className="font-medium text-sm">Enter a mobile number to search all message history</p>
            <p className="text-xs mt-1">Optionally filter by date range. Searches across SMS and WhatsApp — all channels.</p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <div className="text-4xl mb-3">📭</div>
            <p className="font-medium text-sm">No messages found for this number{(fromDate || toDate) ? ' in the selected date range' : ''}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Channel</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Campaign</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Sent At</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-500 uppercase tracking-wide">Message</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className={`border-b border-slate-100 hover:bg-violet-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                    <td className="px-4 py-3 text-slate-400 font-mono whitespace-nowrap">{i + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TYPE_COLOURS[row.Type] || 'bg-slate-100 text-slate-600'}`}>
                        {row.Type}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row.Channel === 'WhatsApp' ? 'bg-emerald-100 text-emerald-700' : 'bg-cyan-100 text-cyan-700'}`}>
                        {row.Channel || 'SMS'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{row.Campaign || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {row.SentDateTime
                        ? new Date(row.SentDateTime).toLocaleString('en-GB', { timeZone: 'UTC' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{row.FirstName || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono whitespace-nowrap">{row.MobileNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        String(row.Status || '').toLowerCase().includes('success') || String(row.Status || '').match(/^0/)
                          ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>{String(row.Status || '').slice(0, 30) || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs">
                      <span className="truncate block" title={row.Message || row.SMSMessage || row.WhatsAppMessage}>
                        {String(row.Message || row.SMSMessage || row.WhatsAppMessage || '').slice(0, 60)}
                        {(row.Message || row.SMSMessage || row.WhatsAppMessage || '').length > 60 ? '…' : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LogsTab ──────────────────────────────────────────────────────────────────



function StatsCards() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/messaging/logs/stats/today')
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    {
      label: 'SMS Sent Today',
      value: stats?.smsSentToday,
      icon: '📱',
    },
    {
      label: 'WA Sent Today',
      value: stats?.waSentToday,
      icon: '💬',
    },
    {
      label: 'Clients via SMS Today',
      value: stats?.smsClientsToday,
      icon: '👤',
    },
    {
      label: 'Clients via WA Today',
      value: stats?.waClientsToday,
      icon: '👥',
    },
    {
      label: 'WA Free Today',
      value: stats?.waFreeSentToday,
      icon: '🆓',
    },
    {
      label: 'WA Template Today',
      value: stats?.waTemplateSentToday,
      icon: '📋',
    },
    {
      label: 'WA Charge Today',
      value: stats?.waTemplateChargeToday,
      icon: '💸',
      type: 'currency',
    },
  ];

  const formatValue = (card) => {
    const value = Number(card.value ?? 0);

    if (card.type === 'currency') {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return value.toLocaleString();
  };

  return (
    <>
      {cards.map(card => (
        <div
          key={card.label}
          className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/25"
        >
          <div className="text-xs text-cyan-200 uppercase tracking-wide mb-1 flex items-center justify-center gap-1">
            <span>{card.icon}</span>
            <span>{card.label}</span>
          </div>

          {loading ? (
            <div className="flex justify-center mt-1">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          ) : (
            <div className="text-2xl font-bold text-white">
              {formatValue(card)}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function LogsTab() {
  const [mode, setMode] = useState('reports');

//   const [logType, setLogType]   = useState('reminders');
//   const [fromDate, setFromDate] = useState('');
//   const [toDate, setToDate]     = useState('');
//   const [page, setPage]         = useState(1);
//   const [data, setData]         = useState([]);
//   const [total, setTotal]       = useState(0);
//   const [loading, setLoading]   = useState(false);
//   const LIMIT = 20;

//   const selectedType = LOG_TYPES.find(t => t.value === logType);
//   const isPromo = !!selectedType?.promoView;

//   const pageRef = useRef(1);

// const fetchLogs = useCallback(async (pageOverride) => {
//   if (isPromo || mode !== 'logs') return;
//   const currentPage = pageOverride ?? pageRef.current;
//   setLoading(true);
//   try {
//     const params = new URLSearchParams({ page: currentPage, limit: LIMIT });
//     if (fromDate) params.set('from', fromDate);
//     if (toDate)   params.set('to', toDate);
//     const r = await api.get(`/messaging/logs/${logType}?${params}`);
//     setData(r.data.data || []);
//     setTotal(r.data.total || 0);
//   } catch {
//     setData([]);
//     setTotal(0);
//   } finally {
//     setLoading(false);
//   }
// }, [logType, fromDate, toDate, isPromo, mode]);

// // Single effect — fires when filters change (reset to page 1) or mode changes
// useEffect(() => {
//   pageRef.current = 1;
//   setPage(1);
//   fetchLogs(1);
// }, [logType, fromDate, toDate, mode]); // eslint-disable-line react-hooks/exhaustive-deps

// // Separate effect — fires only when page changes via pagination buttons
// useEffect(() => {
//   pageRef.current = page;
//   fetchLogs(page);
// }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

//   const pages = Math.ceil(total / LIMIT);
//   const cols = data.length > 0
//     ? Object.keys(data[0]).filter(k => k !== 'Id' && k !== 'SMSMessage' && k !== 'WhatsAppMessage')
//     : [];
//   const msgKey = data[0]
//     ? (data[0].SMSMessage !== undefined ? 'SMSMessage' : data[0].WhatsAppMessage !== undefined ? 'WhatsAppMessage' : null)
//     : null;

  const MODE_TABS = [
    { id: 'reports', label: 'Reports',          icon: '📊' },
    { id: 'search',  label: 'Search by Mobile', icon: '🔎' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-1.5 flex gap-1">
        {MODE_TABS.map(m => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-xs whitespace-nowrap transition-all flex-1 justify-center ${
              mode === m.id
                ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}
          >
            <span>{m.icon}</span>
            <span>{m.label}</span>
          </button>
        ))}
      </div>

      {mode === 'reports' && <ReportsView />}
      {mode === 'search' && <SearchByMobileView />}
    </div>
  );
}

// ─── Manual Send Tab ──────────────────────────────────────────────────────────

const MODULES = [
  { id: 'reminders', label: 'Reminders', icon: '🔔', desc: 'Send appointment reminders (SMS + WhatsApp)' },
  { id: 'birthdays', label: 'Birthdays', icon: '🎂', desc: 'Send birthday greetings' },
];
function ManualTab({ showToast }) {
  const [date, setDate]       = useState('');
  const [dateError, setDateError] = useState('');
  const [running, setRunning] = useState({});
  const [results, setResults] = useState({});

  const run = async (moduleId) => {
    if (!date) {
      setDateError('Please select a date before running.');
      return;
    }
    setDateError('');
    setRunning(p => ({ ...p, [moduleId]: true }));
    setResults(p => ({ ...p, [moduleId]: null }));
    try {
      const r = await api.post(`/messaging/run/${moduleId}`, { date: date.replace(/-/g, '/') });
      setResults(p => ({ ...p, [moduleId]: r.data }));
      const counts = Object.entries(r.data)
        .filter(([k]) => k !== 'ok' && k !== 'module' && k !== 'date')
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      showToast(`${moduleId} — ${counts || 'done'}`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || `Failed: ${moduleId}`, 'error');
      setResults(p => ({ ...p, [moduleId]: { error: err.response?.data?.error } }));
    } finally {
      setRunning(p => ({ ...p, [moduleId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
              📅 Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={e => { setDate(e.target.value); setDateError(''); }}
              className={`px-3.5 py-2.5 border rounded-lg focus:ring-2 outline-none text-slate-800 text-sm transition-all ${
                dateError
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
                  : 'border-slate-200 focus:border-cyan-400 focus:ring-cyan-100'
              }`}
            />
          </div>
          {date && (
            <div className="mt-5 px-3.5 py-2.5 bg-cyan-50 border border-cyan-200 rounded-lg text-sm text-cyan-700 font-medium">
              Running for: <strong>{date}</strong>
            </div>
          )}
        </div>
        {dateError && (
          <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1 bg-red-50 px-2 py-1.5 rounded-lg">
            ⚠️ {dateError}
          </p>
        )}
        {!date && (
          <p className="mt-2 text-xs text-slate-400">Select a date — the module will run only for that specific date.</p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {MODULES.map(mod => {
          const res = results[mod.id];
          const isRunning = running[mod.id];
          return (
            <div key={mod.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-cyan-300 transition-all">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">{mod.icon}</span>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">{mod.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{mod.desc}</div>
                </div>
              </div>
              {res && !res.error && (
                <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700 font-medium flex flex-wrap gap-2">
                  {Object.entries(res)
                    .filter(([k]) => !['ok', 'module', 'date'].includes(k))
                    .map(([k, v]) => (
                      <span key={k} className="bg-emerald-100 px-2 py-0.5 rounded-full">{k}: {v}</span>
                    ))}
                </div>
              )}
              {res?.error && (
                <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">⚠️ {res.error}</div>
              )}
              <button
                onClick={() => run(mod.id)}
                disabled={isRunning}
                className={`w-full py-2.5 font-semibold rounded-lg text-sm flex items-center justify-center gap-2 transition-all ${
                  isRunning
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow hover:shadow-md hover:scale-[1.01] active:scale-100'
                }`}
              >
                {isRunning ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Running…</>
                ) : (
                  <>▶ Run for {date || '…'}</>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ─── SMS Gateway Tab ──────────────────────────────────────────────────────────

function SmsTab({ data, onChange, onSave, saving }) {
  const SMS_DEFAULTS = {
    GatewayURL: 'http://mshastra.com/sendurlcomma.aspx?',
  };

  const f = key => ({
    value: data[key] ?? SMS_DEFAULTS[key] ?? '',
    onChange: e => onChange(key, e.target.value),
  });
  return (
    <div className="space-y-5">
      <Section icon="📡" title="SMS Gateway Configuration" accent="cyan">
        {/* <p className="text-xs text-slate-500 bg-cyan-50 rounded-lg px-3 py-2 border border-cyan-200">
          Credentials used for all outbound SMS messages. The gateway URL should end with <code className="bg-cyan-100 px-1 rounded">?</code> or <code className="bg-cyan-100 px-1 rounded">&amp;</code>.
        </p> */}
        <Field label="Gateway URL" icon="🌐">
          <Input placeholder="https://gateway.example.com/send?" {...f('GatewayURL')} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Username" icon="👤"><Input placeholder="SMS account username" {...f('UserName')} /></Field>
          <Field label="Password" icon="🔑"><Input type="password" placeholder="SMS account password" {...f('Password')} /></Field>
          <Field label="Sender ID" icon="📤"><Input placeholder="e.g. MySalon" {...f('SenderID')} /></Field>
          <Field label="Country Code" icon="🌍"><Input placeholder="e.g. +971" {...f('CountryCode')} /></Field>
        </div>
      </Section>
      <Section icon="📣" title="Promotional Account" accent="amber" badge="Optional">
        <p className="text-xs text-slate-500 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
          Used for bulk promotion campaigns. When not set, falls back to the main account above.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Promo Username" icon="👤"><Input placeholder="Promo account username" {...f('PromoUserName')} /></Field>
          <Field label="Promo Password" icon="🔑"><Input type="password" placeholder="Promo account password" {...f('PromoPassword')} /></Field>
          <Field label="Promo Sender ID" icon="📤"><Input placeholder="e.g. MySalonPromo" {...f('PromoSenderID')} /></Field>
        </div>
      </Section>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}
function LogoUploader({ data, onChange, onSave, saving }) {
  const [preview, setPreview]   = useState(data.LogoImage ? `data:${data.LogoMimeType || 'image/png'};base64,${data.LogoImage}` : null);
  const [dragging, setDragging] = useState(false);
  const [removing, setRemoving] = useState(false);
  const inputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2 MB.'); return; }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;                        // data:image/png;base64,XXXX
      const [header, b64] = dataUrl.split(',');
      const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
      onChange('LogoImage',    b64);
      onChange('LogoMimeType', mime);
      setPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const removeLogo = () => {
    setRemoving(true);
    onChange('LogoImage',    '');
    onChange('LogoMimeType', '');
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
    setRemoving(false);
  };

  // Keep preview in sync if parent reloads settings
  useEffect(() => {
    if (data.LogoImage) {
      setPreview(`data:${data.LogoMimeType || 'image/png'};base64,${data.LogoImage}`);
    } else {
      setPreview(null);
    }
  }, [data.LogoImage, data.LogoMimeType]);

  return (
    <div className="space-y-3">
      {/* Drop zone / preview */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !preview && inputRef.current?.click()}
        className={`relative flex items-center justify-center rounded-xl border-2 transition-all cursor-pointer overflow-hidden
          ${preview
            ? 'border-emerald-200 bg-slate-50 cursor-default h-28'
            : dragging
              ? 'border-emerald-400 bg-emerald-50 h-28'
              : 'border-dashed border-slate-300 hover:border-emerald-300 hover:bg-emerald-50/40 h-28'
          }`}
      >
        {preview ? (
          <>
            <img src={preview} alt="Salon logo" className="max-h-24 max-w-full object-contain p-2" />
            <button
              onClick={e => { e.stopPropagation(); removeLogo(); }}
              disabled={removing}
              className="absolute top-2 right-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold transition-colors border border-red-200"
              title="Remove logo"
            >✕</button>
          </>
        ) : (
          <div className="text-center px-4">
            <div className="text-3xl mb-1">🖼️</div>
            <p className="text-xs font-semibold text-slate-500">
              {dragging ? 'Drop to upload' : 'Click or drag & drop your logo'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">PNG or JPG · max 2 MB · appears top-right on PDF receipts</p>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={e => processFile(e.target.files[0])}
      />

      <div className="flex items-center gap-2">
        <button
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:border-emerald-300 hover:text-emerald-700 transition-all"
        >
          📁 {preview ? 'Replace logo' : 'Choose file'}
        </button>
        {preview && (
          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg">
            ✓ Logo ready — save settings to apply
          </span>
        )}
      </div>
    </div>
  );
}

// ─── WhatsApp Tab ─────────────────────────────────────────────────────────────

function WaTab({ data, onChange, onSave, saving }) {
  const WA_DEFAULTS = {
    SendMsgURL:       'https://app.pickyassist.com/api/v2/push',
    ApplicationId:    '121',
    GetTemplatesURL:  'https://app.pickyassist.com/api/v2/template-status',
  };

  const f = key => ({
    value: data[key] ?? WA_DEFAULTS[key] ?? '',
    onChange: e => onChange(key, e.target.value),
  });
  return (
    <div className="space-y-5">
      <Section icon="💬" title="WhatsApp API Configuration" accent="emerald">
        {/* <p className="text-xs text-slate-500 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
          PickyAssist / Anantya compatible. Supports free-text messages with automatic template fallback.
        </p> */}
        <Field label="Send Message URL" icon="🌐">
          <Input placeholder="https://send.whatsapp.com/send" {...f('SendMsgURL')} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="API Key" icon="🔑"><Input placeholder="Your WhatsApp API key" {...f('APIKey')} /></Field>
          <Field label="Application ID" icon="🆔" hint="Default: 121"><Input type="number" placeholder="1" {...f('ApplicationId')} /></Field>
          <Field label="Country Code" icon="🌍" hint="Used to normalize mobile numbers for WhatsApp, e.g. 971">
            <Input placeholder="e.g. 971" {...f('CountryCode')} />
          </Field>
        </div>
        <Field label="Get Templates URL" icon="📋">
          <Input placeholder="https://templates.pickyassist.com" {...f('GetTemplatesURL')} />
        </Field>
      </Section>
      {/* ── NEW: Logo uploader ── */}
      <Section icon="🖼️" title="Receipt Logo" accent="emerald" badge="PDF Receipts">
        <p className="text-xs text-slate-500 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
          Displayed top-right on WhatsApp PDF receipts. Business details appear on the left. Store as PNG for best quality (MAX SIZE 10MB).
        </p>
        <LogoUploader data={data} onChange={onChange} />
      </Section>
      <Section icon="📝" title="Template IDs" accent="indigo" badge="Fallback">
        <p className="text-xs text-slate-500 bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-200">
          Used as fallback when free-text fails (outside the 24h customer service window).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Reminder Template ID" icon="🔔"><Input placeholder="Template ID for reminders" {...f('ReminderTemplateID')} /></Field>
          <Field label="Pre-Arrival Template ID" icon="⏰"><Input placeholder="Template ID for pre-arrival" {...f('PreArrivalTemplateID')} /></Field>
          <Field label="Confirmation Template ID" icon="✅"><Input placeholder="Template ID for confirmations" {...f('ConfirmationTemplateID')} /></Field>
          <Field label="Thank You Template ID" icon="🙏"><Input placeholder="Template ID for thank you" {...f('ThankYouTemplateID')} /></Field>
          <Field label="Thank You + Receipt Template ID" icon="🧾">
            <Input placeholder="Template ID for thank you with PDF receipt" {...f('ThankYouPdfTemplateID')} />
          </Field>
          <Field label="Birthday Template ID" icon="🎂"><Input placeholder="Template ID for birthdays" {...f('BirthdayTemplateID')} /></Field>
          <Field label="New Client Template ID" icon="👋"><Input placeholder="Template ID for new clients" {...f('NewClientTemplateID')} /></Field>
          <Field label="Referral Template ID" icon="🤝"><Input placeholder="Template ID for referrals" {...f('ReferralTemplateID')} /></Field>
          <Field label="Series Expiry Template ID" icon="📦"><Input placeholder="Template ID for series expiry" {...f('SeriesExpiryTemplateID')} /></Field>
          <Field label="GC Expiry Template ID" icon="🎁"><Input placeholder="Template ID for GC expiry" {...f('GCExpiryTemplateID')} /></Field>
          <Field label="Miss You Template ID" icon="💔"><Input placeholder="Template ID for miss you" {...f('MissYouTemplateID')} /></Field>
          <Field label="No Show Template ID" icon="🚫"><Input placeholder="Template ID for no show" {...f('NoShowTemplateID')} /></Field>
          
          <Field label="Promotion Template ID" icon="📣">
            <Input placeholder="Template ID for bulk promotion campaigns" {...f('PromotionTemplateID')} />
          </Field>
        </div>
      </Section>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}
// Available template-var placeholders per module (only modules that support WA templates with data need this,
// starting with thankyou since it has multiple dynamic fields)
const MODULE_TEMPLATE_VARS = {
  reminder: [
    { key: '@FirstName',    label: 'First Name' },
    { key: '@Date',         label: 'Date' },
    { key: '@Time',         label: 'Time' },
    { key: '@ServiceName',  label: 'Service Name' },
    { key: '@EmployeeName', label: 'Employee Name' },
  ],

  prearrival: [
    { key: '@FirstName',    label: 'First Name' },
    { key: '@Date',         label: 'Date' },
    { key: '@Time',         label: 'Time' },
    { key: '@ServiceName',  label: 'Service Name' },
    { key: '@EmployeeName', label: 'Employee Name' },
  ],

  confirmation: [
    { key: '@FirstName',    label: 'First Name' },
    { key: '@Date',         label: 'Date' },
    { key: '@Time',         label: 'Time' },
    { key: '@ServiceName',  label: 'Service Name' },
    { key: '@EmployeeName', label: 'Employee Name' },
  ],

  thankyou: [
    { key: '@FirstName',   label: 'First Name' },
    { key: '@TotalPoints', label: 'Total Points' },
    { key: '@TodayPoints', label: 'Today Points' },
    { key: '@Amount',      label: 'Amount' },
    { key: '@PaymentType', label: 'Payment Type' },
  ],
};

const MODULE_TEMPLATE_VAR_SETTING_KEYS = {
  reminder: 'RWaTemplateVars',
  prearrival: 'PreArrivalWaTemplateVars',
  confirmation: 'CWaTemplateVars',
  thankyou: 'TYWaTemplateVars',
};
// ─── Automation Tab ───────────────────────────────────────────────────────────


const PLACEHOLDERS = {
  reminder:     '@FirstName @Date @Time @ServiceName @EmployeeName',
  confirmation: '@FirstName @Date @Time @ServiceName @EmployeeName',
  thankyou:     '@FirstName @TotalPoints @TodayPoints @Amount @PaymentType',
  newclients:   '@FirstName',
  birthdays:    '@FirstName',
  referrals:    '@FirstName @RFirstName',
  seriesexpiry: '@FirstName @PackageName @Remaining @ExpiryDate',
  gcexpiry:     '@FirstName @PurchaseDate @OriginalAmount @OutstandingAmount @ExpiryDate',
  missyou:      '@FirstName',
  noshow:       '@FirstName',
  prearrival:   '@FirstName @Date @Time @ServiceName @EmployeeName',
};

const MODULE_CONFIGS = [
  {
  key: 'reminder',
  smsActiveFlag: 'ARActive',
  waActiveFlag: 'WARActive',
  label: 'Appointment Reminders',
  icon: '🔔',
  accent: 'cyan',
  hasTime: true,
  hourKey: 'RHour',
  minKey: 'RMinutes',
  daysKey: 'RDIA',
  daysLabel: 'Days in Advance',
  smsMsgKey: 'RMessage',
  waMsgKey: 'WARMessage',
  smsPromoKey: 'RUsePromo',
  waTemplateIdKey: 'ReminderTemplateID',

  waTemplateVarsKey: 'RWaTemplateVars',
  waTemplateOnlyKey: 'RWaTemplateOnly',
},
  {
    key: 'prearrival', smsActiveFlag: 'PreArrivalActive', waActiveFlag: 'WAPreArrivalActive',
    label: 'Pre-Arrival', icon: '⏰', accent: 'indigo',
    renderedSeparately: true,
  },
  {
  key: 'confirmation',
  smsActiveFlag: 'ACActive',
  waActiveFlag: 'WACActive',
  label: 'Booking Confirmations',
  icon: '✅',
  accent: 'emerald',
  hasTime: false,
  smsMsgKey: 'CMessage',
  waMsgKey: 'WACMessage',
  smsPromoKey: 'CUsePromo',
  waTemplateIdKey: 'ConfirmationTemplateID',

  waTemplateVarsKey: 'CWaTemplateVars',
  waTemplateOnlyKey: 'WACTemplateOnly',
},
  {
    key: 'thankyou', smsActiveFlag: 'TActive', waActiveFlag: 'WATActive',
    label: 'Thank You', icon: '🙏', accent: 'indigo',
    hasTime: false,
    smsMsgKey: 'TMessage', waMsgKey: 'WATMessage', smsPromoKey: 'TUsePromo',
    waTemplateIdKey: 'ThankYouTemplateID',
    waTemplateOnlyKey: 'WATTemplateOnly',
  },
  {
    key: 'newclients', smsActiveFlag: 'NCActive', waActiveFlag: 'WANCActive',
    label: 'New Clients', icon: '👋', accent: 'violet',
    hasTime: false,
    smsMsgKey: 'NCMessage', waMsgKey: 'WANCMessage', smsPromoKey: 'NCUsePromo',
    waTemplateIdKey: 'NewClientTemplateID',
    waTemplateOnlyKey: 'WANCTemplateOnly',
  },
  {
    key: 'birthdays', smsActiveFlag: 'BActive', waActiveFlag: 'WABActive',
    label: 'Birthdays', icon: '🎂', accent: 'rose',
    hasTime: true, hourKey: 'BHour', minKey: 'BMinutes', daysKey: 'BDIA', daysLabel: 'Days in Advance',
    smsMsgKey: 'BMessage', waMsgKey: 'WABMessage', smsPromoKey: 'BDUsePromo',
    waTemplateIdKey: 'BirthdayTemplateID',
    waTemplateOnlyKey: 'WABTemplateOnly',
  },
  {
    key: 'referrals', smsActiveFlag: 'RefActive', waActiveFlag: 'WARefActive',
    label: 'Referrals', icon: '🤝', accent: 'amber',
    hasTime: false,
    smsMsgKey: 'RefMessage', waMsgKey: 'WARefMessage', smsPromoKey: 'REFUsePromo',
    waTemplateIdKey: 'ReferralTemplateID',
    waTemplateOnlyKey: 'WARefTemplateOnly',
  },
  {
    key: 'seriesexpiry', smsActiveFlag: 'SExpiryActive', waActiveFlag: 'WASExpiryActive',
    label: 'Series Expiry', icon: '📦', accent: 'cyan',
    hasTime: true, hourKey: 'SExpiryHour', minKey: 'SExpiryMin', daysKey: 'SExpiryDays', daysLabel: 'Days Before Expiry',
    smsMsgKey: 'SExpiryMsg', waMsgKey: 'WASExpiryMsg', smsPromoKey: 'SExpiryUsePromo',
    waTemplateIdKey: 'SeriesExpiryTemplateID',
    waTemplateOnlyKey: 'WASExpiryTemplateOnly',
  },
  {
    key: 'gcexpiry', smsActiveFlag: 'GCExpiryActive', waActiveFlag: 'WAGCExpiryActive',
    label: 'Gift Cert. Expiry', icon: '🎁', accent: 'emerald',
    hasTime: true, hourKey: 'GCExpiryHour', minKey: 'GCExpiryMin', daysKey: 'GCExpiryDays', daysLabel: 'Days Before Expiry',
    smsMsgKey: 'GCExpiryMsg', waMsgKey: 'WAGCExpiryMsg', smsPromoKey: 'GCExpiryUsePromo',
    waTemplateIdKey: 'GCExpiryTemplateID',
    waTemplateOnlyKey: 'WAGCExpiryTemplateOnly',
  },
  {
    key: 'missyou', smsActiveFlag: 'MissYouActive', waActiveFlag: 'WAMissYouActive',
    label: 'Miss You', icon: '💔', accent: 'rose',
    hasTime: true, hourKey: 'MissYouHour', minKey: 'MissYouMin', daysKey: 'MissYouDays', daysLabel: 'Days Since Last Visit',
    smsMsgKey: 'MissYouMsg', waMsgKey: 'WAMissYouMsg', smsPromoKey: 'MissYouUsePromo',
    waTemplateIdKey: 'MissYouTemplateID',
    waTemplateOnlyKey: 'WAMissYouTemplateOnly',
  },
  {
    key: 'noshow', smsActiveFlag: 'NoShowActive', waActiveFlag: 'WANoShowActive',
    label: 'No Show', icon: '🚫', accent: 'amber',
    hasTime: false,
    smsMsgKey: 'NoShowMsg', waMsgKey: 'WANoShowMsg', smsPromoKey: 'NoShowUsePromo',
    waTemplateIdKey: 'NoShowTemplateID',
    waTemplateOnlyKey: 'WANoShowTemplateOnly',
  },
];

function applyTestValues(message, vars) {
  let result = message || '';
  Object.entries(vars).forEach(([key, value]) => {
    result = result.replaceAll(key, value);
  });
  return result;
}

const MODULE_TEST_FIELDS = {
  reminder:     [
    { key: '@FirstName',   label: 'First Name',    default: 'Test' },
    { key: '@Date',        label: 'Date',           default: '15 Jun 2026' },
    { key: '@Time',        label: 'Time',           default: '10:30 AM' },
    { key: '@ServiceName', label: 'Service Name',   default: 'Haircut' },
    { key: '@EmployeeName',label: 'Employee Name',  default: 'John' },
  ],
  confirmation: [
    { key: '@FirstName',   label: 'First Name',    default: 'Test' },
    { key: '@Date',        label: 'Date',           default: '15 Jun 2026' },
    { key: '@Time',        label: 'Time',           default: '10:30 AM' },
    { key: '@ServiceName', label: 'Service Name',   default: 'Haircut' },
    { key: '@EmployeeName',label: 'Employee Name',  default: 'John' },
  ],
  thankyou:     [{ key: '@FirstName', label: 'First Name', default: 'Test' }],
  newclients:   [{ key: '@FirstName', label: 'First Name', default: 'Test' }],
  birthdays:    [{ key: '@FirstName', label: 'First Name', default: 'Test' }],
  referrals:    [
    { key: '@FirstName',  label: 'First Name',         default: 'Test' },
    { key: '@RFirstName', label: 'Referred First Name', default: 'David' },
  ],
  seriesexpiry: [
    { key: '@FirstName',   label: 'First Name',    default: 'Test' },
    { key: '@PackageName', label: 'Package Name',  default: 'Test Package' },
    { key: '@Remaining',   label: 'Remaining',     default: '3' },
    { key: '@ExpiryDate',  label: 'Expiry Date',   default: '30 Jun 2026' },
  ],
  gcexpiry: [
    { key: '@FirstName',        label: 'First Name',        default: 'Test' },
    { key: '@PurchaseDate',     label: 'Purchase Date',     default: '01 Jan 2026' },
    { key: '@OriginalAmount',   label: 'Original Amount',   default: '5000' },
    { key: '@OutstandingAmount',label: 'Outstanding Amount',default: '1200' },
    { key: '@ExpiryDate',       label: 'Expiry Date',       default: '31 Dec 2026' },
  ],
  missyou: [{ key: '@FirstName', label: 'First Name', default: 'Test' }],
  noshow:  [{ key: '@FirstName', label: 'First Name', default: 'Test' }],

  prearrival: [
    { key: '@FirstName',   label: 'First Name',    default: 'Test' },
    { key: '@Date',        label: 'Date',           default: '15 Jun 2026' },
    { key: '@Time',        label: 'Time',           default: '10:30 AM' },
    { key: '@ServiceName', label: 'Service Name',   default: 'Haircut' },
    { key: '@EmployeeName',label: 'Employee Name',  default: 'John' },
  ],
};

function TemplateVarsSelector({ moduleKey, selected, onChange }) {
  const available = MODULE_TEMPLATE_VARS[moduleKey];
  if (!available || available.length === 0) return null;

  const isChecked = (key) => selected.includes(key);

  const toggle = (key) => {
    if (isChecked(key)) {
      onChange(selected.filter(k => k !== key));
    } else {
      onChange([...selected, key]); // appended at the end → preserves selection order
    }
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...selected];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const moveDown = (idx) => {
    if (idx === selected.length - 1) return;
    const next = [...selected];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-emerald-200 overflow-hidden">
      <div className="bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
        <span className="text-sm">🧩</span>
        <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Template Variables to Send</span>
      </div>
      <div className="bg-emerald-50/40 px-4 pb-4 pt-3 space-y-3">
        <p className="text-xs text-slate-500 bg-white rounded-lg px-3 py-2 border border-emerald-200">
          Choose which values get sent as variables to the WhatsApp <strong>template</strong> (not used for free-text). Order below = order sent to the template.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {available.map(v => (
            <label
              key={v.key}
              className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none bg-white border border-emerald-100 rounded-lg px-2.5 py-2 hover:border-emerald-300 transition-colors"
            >
              <input
                type="checkbox"
                checked={isChecked(v.key)}
                onChange={() => toggle(v.key)}
                className="accent-emerald-500 w-3.5 h-3.5"
              />
              <span className="font-medium">{v.label}</span>
              <span className="text-slate-300 font-mono text-xs ml-auto">{v.key}</span>
            </label>
          ))}
        </div>

        {selected.length > 0 && (
          <div className="bg-white rounded-lg border border-emerald-200 p-2.5 space-y-1.5">
            <p className="text-xs font-semibold text-emerald-700">Send order:</p>
            {selected.map((key, idx) => {
              const meta = available.find(a => a.key === key);
              return (
                <div key={key} className="flex items-center gap-2 text-xs bg-emerald-50 rounded-lg px-2.5 py-1.5">
                  <span className="font-mono text-emerald-700 bg-emerald-100 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                  <span className="text-slate-700 font-medium flex-1">{meta?.label || key}</span>
                  <button onClick={() => moveUp(idx)} disabled={idx === 0} className="text-slate-400 hover:text-emerald-600 disabled:opacity-30 px-1">▲</button>
                  <button onClick={() => moveDown(idx)} disabled={idx === selected.length - 1} className="text-slate-400 hover:text-emerald-600 disabled:opacity-30 px-1">▼</button>
                  <button onClick={() => onChange(selected.filter(k => k !== key))} className="text-red-400 hover:text-red-600 px-1">✕</button>
                </div>
              );
            })}
          </div>
        )}

        {selected.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ⚠️ No variables selected — the template will be sent with an empty variable list.
          </p>
        )}
      </div>
    </div>
  );
}

function InlineTestPanel({ channel, moduleKey, msgKey, data, waTemplateId, templateVarsOrder }) {
  const [open, setOpen]       = useState(false);
  const [mobile, setMobile]   = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult]   = useState(null);
  const [waMode, setWaMode]   = useState('free');

  const fields = MODULE_TEST_FIELDS[moduleKey] || [{ key: '@FirstName', label: 'First Name', default: 'Test' }];
  const [testVarValues, setTestVarValues] = useState(
    () => Object.fromEntries(fields.map(f => [f.key, f.default]))
  );

  // Reset vars if moduleKey changes
  useEffect(() => {
    const f = MODULE_TEST_FIELDS[moduleKey] || [{ key: '@FirstName', label: 'First Name', default: 'Test' }];
    setTestVarValues(Object.fromEntries(f.map(ff => [ff.key, ff.default])));
  }, [moduleKey]);

  const renderResult = (r) => {
    const v = r?.result ?? r?.error;
    if (v == null) return JSON.stringify(r);
    if (typeof v === 'string' || typeof v === 'number') return v;
    return JSON.stringify(v);
  };

  const isSms = channel === 'sms';
  const label = isSms ? 'SMS' : 'WhatsApp';
  const icon  = isSms ? '📱' : '💬';
  const hint  = isSms
    ? 'Digits only, no country prefix — e.g. 501234567'
    : 'Include country code — e.g. 971501234567';

  const resolvedTemplateId = isSms ? '' : (waTemplateId || '').trim();
  const hasTemplate = !isSms && resolvedTemplateId.length > 0;

  const isSessionError = (res) => {
  if (!res) return false;
  const raw = res.error ?? res.result;
  const s = typeof raw === 'string' ? raw : JSON.stringify(raw ?? res);
  return s.toLowerCase().includes('session') || s.toLowerCase().includes('24') || s.toLowerCase().includes('expired') || s.toLowerCase().includes('window');
};

  // Resolve which keys (and in what order) get sent as template vars.
  // Falls back to all fields in default order if no selection has been made yet.
  const effectiveVarOrder = (templateVarsOrder && templateVarsOrder.length > 0)
    ? templateVarsOrder
    : fields.map(f => f.key);

  const sendTest = async () => {
    if (!mobile) return;
    setSending(true);
    setResult(null);
    try {
      const rawMessage = (data[msgKey] || '').trim();
      const message = applyTestValues(rawMessage, testVarValues);
      if (isSms) {
        const usePromoKeyMap = {
  reminders: 'RUsePromo',
  prearrival: 'PreArrivalUsePromo',
  confirmations: 'CUsePromo',
  thankyou: 'TUsePromo',
  newclients: 'NCUsePromo',
  birthdays: 'BDUsePromo',
  referrals: 'REFUsePromo',
  seriesexpiry: 'SExpiryUsePromo',
  gcexpiry: 'GCExpiryUsePromo',
  missyou: 'MissYouUsePromo',
  noshow: 'NoShowUsePromo'
};

const usePromoKey = usePromoKeyMap[moduleKey];
const usePromo = usePromoKey ? Boolean(data[usePromoKey]) : false;

const r = await api.post('/messaging/test/sms', {
  mobile,
  message,
  module: moduleKey,
  usePromo
});
        setResult(r.data);
      } else if (waMode === 'free') {
        const r = await api.post('/messaging/test/whatsapp', { mobile, message });
        setResult(r.data);
      } else {
        const vars = effectiveVarOrder.map(key => testVarValues[key] ?? '');
        const r = await api.post('/messaging/test/whatsapp-template', {
          mobile,
          templateId: resolvedTemplateId,
          vars,
          message,
        });
        setResult(r.data);
      }
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  const accentBorder = isSms ? 'border-cyan-200'   : 'border-emerald-200';
  const accentBg     = isSms ? 'bg-cyan-50'         : 'bg-emerald-50';
  const accentText   = isSms ? 'text-cyan-700'      : 'text-emerald-700';
  const accentBtn    = isSms ? 'from-cyan-500 to-blue-500' : 'from-emerald-500 to-teal-500';
  const accentFocus  = isSms ? 'focus:border-cyan-400 focus:ring-cyan-100' : 'focus:border-emerald-400 focus:ring-emerald-100';
  const showSessionBanner = !isSms && result && isSessionError(result) && waMode === 'free';
  const canSend = mobile && (!(!isSms && waMode === 'template' && !hasTemplate));

  return (
    <div className={`mt-3 rounded-lg border ${accentBorder} overflow-hidden`}>
      <button
        onClick={() => { setOpen(o => !o); setResult(null); }}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold ${accentBg} ${accentText} transition-colors`}
      >
        <span className="flex items-center gap-1.5">{icon} Test {label} Message</span>
        <span className="opacity-60">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={`px-3 pb-3 pt-2 ${accentBg} space-y-2.5`}>
          {!isSms && (
            <div className="flex rounded-lg border border-emerald-200 overflow-hidden text-xs font-semibold">
              <button
                onClick={() => { setWaMode('free'); setResult(null); }}
                className={`flex-1 py-2 flex items-center justify-center gap-1.5 transition-colors ${waMode === 'free' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
              >
                💬 Free Text
              </button>
              <button
                onClick={() => { setWaMode('template'); setResult(null); }}
                className={`flex-1 py-2 flex items-center justify-center gap-1.5 border-l border-emerald-200 transition-colors ${waMode === 'template' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
              >
                📋 Template
              </button>
            </div>
          )}
          {showSessionBanner && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              <span className="mt-0.5">⚠️</span>
              <div>
                <p className="font-semibold">24-hour session expired</p>
                <p className="mt-0.5 opacity-90">Free-text requires an open customer service window. Switch to <strong>Template</strong> mode to send outside that window.</p>
              </div>
            </div>
          )}

          {/* ── Test variable fields ── */}
          <div className={`rounded-lg border ${accentBorder} bg-white/70 p-2.5 space-y-2`}>
            <p className={`text-xs font-semibold ${accentText} mb-1`}>✏️ Test Values</p>
            <div className="grid grid-cols-2 gap-2">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-slate-400 mb-0.5 font-medium">{f.label}</label>
                  <input
                    type="text"
                    value={testVarValues[f.key] ?? f.default}
                    onChange={e => setTestVarValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className={`w-full px-2.5 py-1.5 border ${accentBorder} rounded-lg focus:ring-2 outline-none text-slate-800 text-xs bg-white ${accentFocus}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={`block text-xs font-semibold mb-1 ${accentText}`}>📞 Mobile Number</label>
            <input
              type="tel"
              placeholder={isSms ? '501234567' : '971501234567'}
              value={mobile}
              onChange={e => setMobile(e.target.value)}
              className={`w-full px-3 py-2 border ${accentBorder} rounded-lg focus:ring-2 outline-none text-slate-800 text-sm bg-white ${accentFocus}`}
            />
            <p className="text-xs text-slate-400 mt-1">{hint}</p>
          </div>

          {!isSms && waMode === 'template' && (
            <div>
              <label className="block text-xs font-semibold mb-1 text-emerald-700">📋 Template ID</label>
              {hasTemplate ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-emerald-200 rounded-lg bg-white">
                  <span className="font-mono text-sm text-slate-700 flex-1">{resolvedTemplateId}</span>
                  <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                    From WA Settings ✓
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2.5 border border-amber-200 rounded-lg bg-amber-50">
                  <span className="text-base">⚠️</span>
                  <div>
                    <p className="text-xs font-semibold text-amber-800">No template configured</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Set a Template ID for this message type in the <strong>WhatsApp</strong> tab to enable template testing.
                    </p>
                  </div>
                </div>
              )}
              {templateVarsOrder && templateVarsOrder.length > 0 && (
                <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                  📤 Sending {templateVarsOrder.length} var{templateVarsOrder.length !== 1 ? 's' : ''} to template, in order:{' '}
                  <span className="font-mono">{templateVarsOrder.join(', ')}</span>
                </p>
              )}
            </div>
          )}

          <p className={`text-xs ${accentText} bg-white/70 rounded px-2 py-1.5 border ${accentBorder}`}>
            {isSms
              ? 'Sends the SMS message above with test values substituted in.'
              : waMode === 'free'
                ? 'Sends the WhatsApp message with test values as free text. Requires an open 24h customer service window.'
                : 'Sends via the template endpoint — works outside the 24h window.'}
          </p>
          <button
            onClick={sendTest}
            disabled={sending || !canSend}
            className={`w-full py-2 font-semibold rounded-lg text-xs flex items-center justify-center gap-2 transition-all ${sending || !canSend ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : `bg-gradient-to-r ${accentBtn} text-white shadow hover:shadow-md`}`}
          >
            {sending
              ? <><div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
              : <>{icon} Send Test {!isSms && waMode === 'template' ? 'via Template' : label}</>}
          </button>
          {result && (
            <div className={`rounded-lg border px-3 py-2.5 text-xs font-medium flex items-start gap-2 ${result.ok && !result.error ? 'bg-white border-emerald-200 text-emerald-800' : 'bg-white border-red-200 text-red-800'}`}>
              <span>{result.ok === true ? '✅' : '❌'}</span>
              <div>
                <div className="font-bold uppercase tracking-wide text-xs mb-0.5">{result.ok && !result.error ? 'Sent successfully' : 'Failed'}</div>
                <div className="opacity-90 break-all">{renderResult(result)}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function MessageEditor({ msgKey, placeholders, data, onChange, accent = 'cyan' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Message
        </label>
      </div>

      <Textarea
        value={data[msgKey] ?? ''}
        onChange={e => onChange(msgKey, e.target.value)}
        placeholder={`Message text — placeholders: ${placeholders}`}
        rows={3}
      />

      <p className="text-slate-400 text-xs mt-1.5">
        Placeholders: <span className="font-mono">{placeholders}</span>
      </p>
    </div>
  );
}

function ModuleCard({ cfg, data, onChange, waSettings }) {
  const smsOn = !!data[cfg.smsActiveFlag];
  const waOn  = !!data[cfg.waActiveFlag];
  const anyOn = smsOn || waOn;

  const [smsCollapsed, setSmsCollapsed] = useState(true);
  const [waCollapsed, setWaCollapsed]   = useState(true);

  const waTemplateId =
  waSettings?.[cfg.waTemplateIdKey] || '';

const templateVarsSettingKey =
  cfg.waTemplateVarsKey ||
  MODULE_TEMPLATE_VAR_SETTING_KEYS[cfg.key] ||
  null;

const hasTemplateVarOptions =
  !!templateVarsSettingKey &&
  !!MODULE_TEMPLATE_VARS[cfg.key];

const selectedTemplateVars = templateVarsSettingKey
  ? String(data[templateVarsSettingKey] || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  : [];

  const gradients = {
    cyan:    'from-cyan-500 to-blue-500',
    indigo:  'from-indigo-500 to-purple-500',
    emerald: 'from-emerald-500 to-teal-500',
    amber:   'from-amber-400 to-orange-500',
    rose:    'from-rose-500 to-pink-500',
    violet:  'from-violet-500 to-purple-600',
  };

  return (
    <div className={`bg-white rounded-xl border-2 transition-all overflow-hidden ${anyOn ? 'border-slate-300 shadow-sm' : 'border-slate-200'}`}>
      <div className={`px-4 py-3.5 flex items-center justify-between ${anyOn ? `bg-gradient-to-r ${gradients[cfg.accent]}` : 'bg-slate-50'}`}>
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{cfg.icon}</span>
          <span className={`font-semibold text-sm ${anyOn ? 'text-white' : 'text-slate-600'}`}>{cfg.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onChange(cfg.smsActiveFlag, !smsOn)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              smsOn
                ? 'bg-white text-cyan-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-cyan-300 hover:text-cyan-600'
            }`}
          >
            📱 SMS {smsOn && '✓'}
          </button>
          <button
            onClick={() => onChange(cfg.waActiveFlag, !waOn)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              waOn
                ? 'bg-white text-emerald-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'
            }`}
          >
            💬 WA {waOn && '✓'}
          </button>
        </div>
      </div>
      {anyOn && (
        <div className="p-4 space-y-4">
          {cfg.hasTime && (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Hour" icon="🕐">
                <Input type="number" min={0} max={23} placeholder="0"
                  value={data[cfg.hourKey] ?? 0}
                  onChange={e => onChange(cfg.hourKey, parseInt(e.target.value, 10))}
                  className="text-center font-semibold"
                />
              </Field>
              <Field label="Min" icon="⏱️">
                <Input type="number" min={0} max={59} placeholder="0"
                  value={data[cfg.minKey] ?? 0}
                  onChange={e => onChange(cfg.minKey, parseInt(e.target.value, 10))}
                  className="text-center font-semibold"
                />
              </Field>
              <Field label={cfg.daysLabel || 'Days'} icon="📅">
                <Input type="number" min={0} placeholder="1"
                  value={data[cfg.daysKey] ?? 0}
                  onChange={e => onChange(cfg.daysKey, parseInt(e.target.value, 10))}
                  className="text-center font-semibold"
                />
              </Field>
            </div>
          )}
          {smsOn && (
            <div className="rounded-xl border border-cyan-100 overflow-hidden">
              <button
                onClick={() => setSmsCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-cyan-50/80 hover:bg-cyan-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">📱</span>
                  <span className="text-xs font-bold text-cyan-700 uppercase tracking-wide">SMS Message</span>
                </div>
                <span className="text-cyan-400 text-xs font-bold select-none">{smsCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>
              {!smsCollapsed && (
                <div className="bg-cyan-50/40 px-4 pb-4 pt-3 space-y-3">
                  <MessageEditor
                    msgKey={cfg.smsMsgKey}
                    placeholders={PLACEHOLDERS[cfg.key]}
                    data={data}
                    onChange={onChange}
                    accent="cyan"
                  />
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none w-fit">
                    <input
                      type="checkbox"
                      checked={!!data[cfg.smsPromoKey]}
                      onChange={e => onChange(cfg.smsPromoKey, e.target.checked)}
                      className="accent-cyan-500 w-3.5 h-3.5"
                    />
                    <span className="font-medium">Use Promotional SMS Account</span>
                  </label>
                  <InlineTestPanel channel="sms" moduleKey={cfg.key} msgKey={cfg.smsMsgKey} data={data} />
                </div>
              )}
            </div>
          )}
          {waOn && (
            <div className="rounded-xl border border-emerald-100 overflow-hidden">
              <button
                onClick={() => setWaCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-50/80 hover:bg-emerald-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">💬</span>
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">WhatsApp Message</span>
                </div>
                <span className="text-emerald-400 text-xs font-bold select-none">{waCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>
              {!waCollapsed && (
                <div className="bg-emerald-50/40 px-4 pb-4 pt-3 space-y-3">
                  <MessageEditor
                    msgKey={cfg.waMsgKey}
                    placeholders={PLACEHOLDERS[cfg.key]}
                    data={data}
                    onChange={onChange}
                    accent="emerald"
                  />
                  <p className="text-xs text-slate-400 bg-white/70 rounded-lg px-2.5 py-1.5 border border-emerald-100">
                    ℹ️ Supports line breaks and basic formatting. Template fallback is used outside the 24h window.
                  </p>

                 {/* ── E-Invoice QR wait toggles — Thank You module only ── */}
                  {cfg.key === 'thankyou' && (
                    <div className="space-y-2">
                      <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer select-none bg-white border border-emerald-200 rounded-lg px-3 py-2.5 hover:border-emerald-300 transition-colors">
                        <input
                          type="checkbox"
                          checked={!!data.WaitForZatcaQR}
                          onChange={e => onChange('WaitForZatcaQR', e.target.checked)}
                          className="accent-emerald-500 w-3.5 h-3.5 mt-0.5"
                        />
                        <span>
                          <span className="font-semibold text-slate-700">🧾 Wait for ZATCA QR code (Saudi Arabia)</span>
                          <br />
                          <span className="text-slate-400">
                            Enable if this business is ZATCA-registered. The receipt will wait up to ~2 minutes for the QR code to be ready before sending.
                          </span>
                        </span>
                      </label>

                      <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer select-none bg-white border border-emerald-200 rounded-lg px-3 py-2.5 hover:border-emerald-300 transition-colors">
                        <input
                          type="checkbox"
                          checked={!!data.WaitForGtaQR}
                          onChange={e => onChange('WaitForGtaQR', e.target.checked)}
                          className="accent-emerald-500 w-3.5 h-3.5 mt-0.5"
                        />
                        <span>
                          <span className="font-semibold text-slate-700">🧾 Wait for GTA QR code (Qatar)</span>
                          <br />
                          <span className="text-slate-400">
                            Enable if this business is Qatar GTA E-Invoice-registered. The receipt will wait up to ~2 minutes for the QR code to be ready before sending.
                          </span>
                        </span>
                      </label>

                      <p className="text-xs text-slate-400 px-1">
                        Leave both off if this business doesn't use e-invoicing. Only one normally applies per business.
                      </p>
                    </div>
                  )}

                  

                  {cfg.waTemplateOnlyKey && (
  <label
    className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
      data[cfg.waTemplateOnlyKey]
        ? 'border-emerald-300 bg-emerald-50'
        : 'border-slate-200 bg-white hover:border-emerald-200'
    }`}
  >
    <input
      type="checkbox"
      checked={Boolean(data[cfg.waTemplateOnlyKey])}
      onChange={(e) =>
        onChange(
          cfg.waTemplateOnlyKey,
          e.target.checked
        )
      }
      className="mt-0.5 h-4 w-4 accent-emerald-500"
    />

    <div>
      <div className="text-sm font-semibold text-slate-700">
        Send approved template only
      </div>

      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
        When enabled, the free-text WhatsApp attempt is skipped and the
        configured approved template is sent directly.
      </p>
    </div>
  </label>
)}

{hasTemplateVarOptions && (
  <TemplateVarsSelector
    moduleKey={cfg.key}
    selected={selectedTemplateVars}
    onChange={(next) =>
      onChange(
        templateVarsSettingKey,
        next.join(',')
      )
    }
  />
)}

{cfg.waTemplateOnlyKey &&
  hasTemplateVarOptions &&
  Boolean(data[cfg.waTemplateOnlyKey]) &&
  selectedTemplateVars.length === 0 && (
    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
      ⚠️ Template-only mode requires at least one template variable.
    </p>
)}

<InlineTestPanel
  channel="wa"
  moduleKey={cfg.key}
  msgKey={cfg.waMsgKey}
  data={data}
  waTemplateId={waTemplateId}
  templateVarsOrder={
    hasTemplateVarOptions
      ? selectedTemplateVars
      : null
  }
/>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!anyOn && (
        <div className="px-4 py-3 text-xs text-slate-400">
          Enable SMS or WhatsApp — or both — to configure this module.
        </div>
      )}
    </div>
  );
}

function PreArrivalCard({ data, onChange, waSettings }) {
  const smsOn = !!data.PreArrivalActive;
  const waOn  = !!data.WAPreArrivalActive;
  const anyOn = smsOn || waOn;

  const [smsCollapsed, setSmsCollapsed] = useState(true);
  const [waCollapsed, setWaCollapsed]   = useState(true);

  const waTemplateId = waSettings?.PreArrivalTemplateID || '';
  const templateVarOptions =
  MODULE_TEMPLATE_VARS.prearrival || [];

const templateVarsSettingKey =
  MODULE_TEMPLATE_VAR_SETTING_KEYS.prearrival;

const selectedTemplateVars = String(
  data[templateVarsSettingKey] || ''
)
  .split(',')
  .map(value => value.trim())
  .filter(Boolean);

const hasTemplateVarOptions =
  templateVarOptions.length > 0;

  const smsMinutesError = (data.PreArrivalMinutesBefore ?? 60) > 1440
  ? 'Minutes before appointment cannot exceed 1440.'
  : '';

const waMinutesError = (data.WAPreArrivalMinutesBefore ?? 0) > 1440
  ? 'Minutes before appointment cannot exceed 1440.'
  : '';

  return (
    <div className={`bg-white rounded-xl border-2 transition-all overflow-hidden ${anyOn ? 'border-slate-300 shadow-sm' : 'border-slate-200'}`}>
      <div className={`px-4 py-3.5 flex items-center justify-between ${anyOn ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : 'bg-slate-50'}`}>
        <div className="flex items-center gap-2.5">
          <span className="text-lg">⏰</span>
          <span className={`font-semibold text-sm ${anyOn ? 'text-white' : 'text-slate-600'}`}>Pre-Arrival</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onChange('PreArrivalActive', !smsOn)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              smsOn
                ? 'bg-white text-cyan-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-cyan-300 hover:text-cyan-600'
            }`}
          >
            📱 SMS {smsOn && '✓'}
          </button>
          <button
            onClick={() => onChange('WAPreArrivalActive', !waOn)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              waOn
                ? 'bg-white text-emerald-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'
            }`}
          >
            💬 WA {waOn && '✓'}
          </button>
        </div>
      </div>
      {anyOn && (
        <div className="p-4 space-y-4">
          <p className="text-xs text-slate-500 bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-200">
            Sent a set number of minutes before the client's scheduled appointment time, independent of the day-based Appointment Reminders above. Maximum 1,440 minutes (24 hours).
          </p>
          {smsOn && (
            <div className="rounded-xl border border-cyan-100 overflow-hidden">
              <button
                onClick={() => setSmsCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-cyan-50/80 hover:bg-cyan-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">📱</span>
                  <span className="text-xs font-bold text-cyan-700 uppercase tracking-wide">SMS Pre-Arrival</span>
                </div>
                <span className="text-cyan-400 text-xs font-bold select-none">{smsCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>
              {!smsCollapsed && (
                <div className="bg-cyan-50/40 px-4 pb-4 pt-3 space-y-3">
                  <Field
                  label="Minutes Before Appointment"
                  icon="⏰"
                  hint={
                    !smsMinutesError
                      ? 'Examples: 15, 30, 60, 120 or 1440 minutes'
                      : null
                  }
                  error={smsMinutesError}
                >
                  <Input
                    type="number"
                    min={0}
                    max={1440}
                    step={1}
                    placeholder="60"
                    value={data.PreArrivalMinutesBefore ?? 60}
                    onChange={e =>
                      onChange(
                        'PreArrivalMinutesBefore',
                        e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                      )
                    }
                    className={`text-center font-semibold ${
                      smsMinutesError
                        ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                        : ''
                    }`}
                  />
                </Field>
                  <MessageEditor
                    msgKey="PreArrivalMessage"
                    placeholders={PLACEHOLDERS.prearrival}
                    data={data}
                    onChange={onChange}
                    accent="cyan"
                  />
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none w-fit">
                    <input
                      type="checkbox"
                      checked={!!data.PreArrivalUsePromo}
                      onChange={e => onChange('PreArrivalUsePromo', e.target.checked)}
                      className="accent-cyan-500 w-3.5 h-3.5"
                    />
                    <span className="font-medium">Use Promotional SMS Account</span>
                  </label>
                  <InlineTestPanel channel="sms" moduleKey="prearrival" msgKey="PreArrivalMessage" data={data} />
                </div>
              )}
            </div>
          )}
          {waOn && (
            <div className="rounded-xl border border-emerald-100 overflow-hidden">
              <button
                onClick={() => setWaCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-50/80 hover:bg-emerald-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">💬</span>
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">WhatsApp Pre-Arrival</span>
                </div>
                <span className="text-emerald-400 text-xs font-bold select-none">{waCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>
              {!waCollapsed && (
                <div className="bg-emerald-50/40 px-4 pb-4 pt-3 space-y-3">
                  <Field
                    label="Minutes Before Appointment (WhatsApp)"
                    icon="⏰"
                    hint={
                      !waMinutesError
                        ? 'Leave blank to use the same timing as SMS above'
                        : null
                    }
                    error={waMinutesError}
                  >
                    <Input
                      type="number"
                      min={0}
                      max={1440}
                      step={1}
                      placeholder={String(data.PreArrivalMinutesBefore ?? 60)}
                      value={data.WAPreArrivalMinutesBefore ?? ''}
                      onChange={e =>
                        onChange(
                          'WAPreArrivalMinutesBefore',
                          e.target.value === ''
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      className={`text-center font-semibold ${
                        waMinutesError
                          ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                          : ''
                      }`}
                    />
                  </Field>
                  <MessageEditor
                    msgKey="WAPreArrivalMessage"
                    placeholders={PLACEHOLDERS.prearrival}
                    data={data}
                    onChange={onChange}
                    accent="emerald"
                  />
                  <p className="text-xs text-slate-400 bg-white/70 rounded-lg px-2.5 py-1.5 border border-emerald-100">
                    ℹ️ Falls back to the Reminder Template ID if no Pre-Arrival Template ID is set in the WhatsApp tab.
                  </p>

                  <label
  className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
    data.WAPreArrivalTemplateOnly
      ? 'border-emerald-300 bg-emerald-50'
      : 'border-slate-200 bg-white hover:border-emerald-200'
  }`}
>
  <input
    type="checkbox"
    checked={Boolean(data.WAPreArrivalTemplateOnly)}
    onChange={(e) => onChange('WAPreArrivalTemplateOnly', e.target.checked)}
    className="mt-0.5 h-4 w-4 accent-emerald-500"
  />
  <div>
    <div className="text-sm font-semibold text-slate-700">
      Send approved template only
    </div>
    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
      When enabled, the free-text WhatsApp attempt is skipped and the
      configured Pre-Arrival template is sent directly.
    </p>
  </div>
</label>

{hasTemplateVarOptions &&
  Boolean(data.WAPreArrivalTemplateOnly) &&
  selectedTemplateVars.length === 0 && (
    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
      ⚠️ Template-only mode requires at least one template variable.
    </p>
)}

{hasTemplateVarOptions && (
  <TemplateVarsSelector
    moduleKey="prearrival"
    selected={selectedTemplateVars}
    onChange={(next) =>
      onChange(templateVarsSettingKey, next.join(','))
    }
  />
)}

                  <InlineTestPanel
                    channel="wa"
                    moduleKey="prearrival"
                    msgKey="WAPreArrivalMessage"
                    data={data}
                    waTemplateId={waTemplateId}
                    templateVarsOrder={
                      hasTemplateVarOptions
                        ? selectedTemplateVars
                        : null
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!anyOn && (
        <div className="px-4 py-3 text-xs text-slate-400">
          Enable SMS or WhatsApp — or both — to configure pre-arrival timing.
        </div>
      )}
    </div>
  );
}
// ─── Char count helper ────────────────────────────────────────────────────────

function charCount(text) {
  const hasUnicode = [...text].some(ch => ch.codePointAt(0) > 255);
  const len = text.length;
  if (len === 0) return { chars: 0, parts: 0, unicode: false };
  const limit = hasUnicode ? 70 : 160;
  return { chars: len, parts: Math.ceil(len / limit), unicode: hasUnicode };
}

// ─── Promotions Card ──────────────────────────────────────────────────────────

function PromotionsCard({  showToast, autoSettings, onAutoChange  }) {
  const [smsOn, setSmsOn]               = useState(false);
  const [waOn,  setWaOn]                = useState(false);
  const [smsCollapsed, setSmsCollapsed] = useState(false);
  const [waCollapsed,  setWaCollapsed]  = useState(false);

  const [campaignName, setCampaignName] = useState('');
  const [smsMessage, setSmsMessage]     = useState('');
  const [waMessage, setWaMessage]       = useState('');

  const [smsTestMobile, setSmsTestMobile] = useState('');
  const [waTestMobile, setWaTestMobile]   = useState('');

  const [campaignCheck, setCampaignCheck] = useState(null);
  const [checkLoading, setCheckLoading]   = useState(false);
  const [skipExisting, setSkipExisting]   = useState(true);
  const [activeYears, setActiveYears] = useState(
  parseInt(autoSettings?.PromoActiveYears, 10) || 0
);

  const [sending, setSending]       = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [progress, setProgress]     = useState(null);
  const [jobError, setJobError]     = useState(null);
  const sseRef = useRef(null);

  const [smsTestSending, setSmsTestSending] = useState(false);
  const [smsTestResult,  setSmsTestResult]  = useState(null);
  const [waTestSending,  setWaTestSending]  = useState(false);
  const [waTestResult,   setWaTestResult]   = useState(null);

  const anyOn = smsOn || waOn;
  const cc = charCount(smsMessage);
  const renderTestResult = (r, resultKey) => {
  const v = r?.results?.[resultKey] ?? r?.result ?? r?.error;
  if (v == null) return JSON.stringify(r);
  if (typeof v === 'string' || typeof v === 'number') return v;
  return JSON.stringify(v);
};
  useEffect(() => {
    setCampaignCheck(null);
    if (!campaignName.trim()) return;
    const t = setTimeout(async () => {
      setCheckLoading(true);
      try {
        const r = await api.get(`/messaging/promotions/campaign-check?name=${encodeURIComponent(campaignName.trim())}`);
        setCampaignCheck(r.data);
      } catch {
        setCampaignCheck(null);
      } finally {
        setCheckLoading(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [campaignName]);

  useEffect(() => () => { if (sseRef.current) sseRef.current.close(); }, []);

  const sendSmsTest = async () => {
    if (!smsTestMobile || !smsMessage.trim()) return;
    setSmsTestSending(true);
    setSmsTestResult(null);
    try {
      const r = await api.post('/messaging/promotions/test', { mobile: smsTestMobile, message: smsMessage, channel: 'sms' });
      setSmsTestResult(r.data);
      if (r.data.ok) showToast('Test SMS sent!', 'success');
    } catch (err) {
      setSmsTestResult({ ok: false, error: err.response?.data?.error || err.message });
      showToast('Test send failed', 'error');
    } finally {
      setSmsTestSending(false);
    }
  };

  const sendWaTest = async () => {
    if (!waTestMobile || !waMessage.trim()) return;
    setWaTestSending(true);
    setWaTestResult(null);
    try {
      const r = await api.post('/messaging/promotions/test', { mobile: waTestMobile, message: waMessage, channel: 'wa' });
      setWaTestResult(r.data);
      if (r.data.ok) showToast('Test WA sent!', 'success');
    } catch (err) {
      setWaTestResult({ ok: false, error: err.response?.data?.error || err.message });
      showToast('Test send failed', 'error');
    } finally {
      setWaTestSending(false);
    }
  };

  const startSend = async () => {
    if (!campaignName.trim()) return;
    if (smsOn && !smsMessage.trim()) return;
    if (waOn  && !waMessage.trim())  return;
    if (!smsOn && !waOn) return;

    setSending(true);
    setSendResult(null);
    setProgress(null);
    setJobError(null);
    try {
      // NEW
const r = await api.post('/messaging/promotions/send', {
  campaignName: campaignName.trim(),
  smsMessage: smsOn ? smsMessage : '',
  waMessage:  waOn  ? waMessage  : '',
  skipExisting,
  activeYears,
});
      const jobId = r.data.jobId;
      const baseURL = api.defaults?.baseURL || '';
      const es = new EventSource(`${baseURL}/messaging/promotions/progress/${jobId}`);
      sseRef.current = es;
      es.onmessage = (e) => {
        const ev = JSON.parse(e.data);
        if (ev.type === 'progress') {
          setProgress(ev);
        } else if (ev.type === 'done') {
        setSendResult(ev);
        setProgress(null);
        setSending(false);
        es.close();
        showToast(`Campaign sent! SMS:${ev.smsSent || 0} WA:${ev.waSent || 0}`, 'success');
      } else if (ev.type === 'error') {
          setJobError(ev.message);
          setSending(false);
          es.close();
          showToast('Campaign send failed', 'error');
        }
      };
      es.onerror = () => {
        setJobError('Connection lost. The campaign may still be running — check the logs.');
        setSending(false);
        es.close();
      };
    } catch (err) {
      setSending(false);
      setJobError(err.response?.data?.error || err.message);
      showToast('Failed to start campaign', 'error');
    }
  };

  const cancelSSE = () => {
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    setSending(false);
    setProgress(null);
    showToast('Disconnected from progress stream. Campaign continues in background.', 'info');
  };

  const pct = progress && progress.total > 0
    ? Math.round(((progress.smsSent || 0) + (progress.waSent || 0) + (progress.skipped || 0)) / progress.total * 100)
    : 0;

  const existsAnywhere = campaignCheck && (campaignCheck.smsExists || campaignCheck.waExists);
  const canSend = !sending && campaignName.trim() &&
                  ((smsOn && smsMessage.trim()) || (waOn && waMessage.trim()));

  return (
    <div className={`bg-white rounded-xl border-2 transition-all overflow-hidden ${anyOn ? 'border-slate-300 shadow-sm' : 'border-slate-200'}`}>
      <div className={`px-4 py-3.5 flex items-center justify-between ${anyOn ? 'bg-gradient-to-r from-fuchsia-500 to-pink-600' : 'bg-slate-50'}`}>
        <div className="flex items-center gap-2.5">
          <span className="text-lg">📣</span>
          <span className={`font-semibold text-sm ${anyOn ? 'text-white' : 'text-slate-600'}`}>Bulk Promotions</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setSmsOn(v => !v)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              smsOn
                ? 'bg-white text-fuchsia-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-fuchsia-300 hover:text-fuchsia-600'
            }`}
          >
            📱 SMS {smsOn && '✓'}
          </button>
          <button
            onClick={() => setWaOn(v => !v)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all ${
              waOn
                ? 'bg-white text-emerald-700 border-white/80 shadow-sm'
                : anyOn
                  ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'
            }`}
          >
            💬 WA {waOn && '✓'}
          </button>
        </div>
      </div>

      {anyOn && (
        <div className="p-4 space-y-4">
          <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/40">
            <Field label="Campaign Name" icon="🏷️" hint="Used to track which clients were already sent this campaign across both channels.">
              <div className="relative">
                <input
                  placeholder="e.g. Summer2026, RamadanOffer"
                  value={campaignName}
                  onChange={e => { setCampaignName(e.target.value); setSendResult(null); }}
                  className={`w-full px-3.5 py-2.5 border rounded-lg focus:ring-2 outline-none text-slate-800 text-sm bg-white transition-all pr-44 ${
                    existsAnywhere
                      ? 'border-amber-300 focus:border-amber-400 focus:ring-amber-100'
                      : 'border-slate-200 focus:border-fuchsia-400 focus:ring-fuchsia-100'
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs font-semibold">
                  {checkLoading && <span className="text-slate-400">Checking…</span>}
                  {!checkLoading && campaignCheck && (
                    <>
                      {campaignCheck.smsExists && (
                        <span className="text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                          SMS:{(campaignCheck.smsSentCount || 0).toLocaleString()}
                        </span>
                      )}
                      {campaignCheck.waExists && (
                        <span className="text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                          WA:{(campaignCheck.waSentCount || 0).toLocaleString()}
                        </span>
                      )}
                      {!campaignCheck.smsExists && !campaignCheck.waExists && (
                        <span className="text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">New ✓</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Field>

            {existsAnywhere && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-lg mt-0.5">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Campaign already exists</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {[
                        campaignCheck.smsExists && `${(campaignCheck.smsSentCount || 0).toLocaleString()} SMS`,
                        campaignCheck.waExists  && `${(campaignCheck.waSentCount  || 0).toLocaleString()} WA`,
                      ].filter(Boolean).join(' and ')} messages were previously sent. Choose how to proceed:
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setSkipExisting(true)}
                    className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                      skipExisting
                        ? 'bg-amber-600 text-white border-amber-600 shadow'
                        : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                    }`}
                  >
                    <span className="text-base">⏭️</span>
                    <div className="text-left">
                      <div>Skip already sent</div>
                      <div className={`font-normal mt-0.5 ${skipExisting ? 'text-amber-100' : 'text-amber-500'}`}>Only send to remaining &amp; new clients</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setSkipExisting(false)}
                    className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                      !skipExisting
                        ? 'bg-amber-600 text-white border-amber-600 shadow'
                        : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                    }`}
                  >
                    <span className="text-base">🔄</span>
                    <div className="text-left">
                      <div>Send to everyone</div>
                      <div className={`font-normal mt-0.5 ${!skipExisting ? 'text-amber-100' : 'text-amber-500'}`}>Re-send to all active clients</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
            {/* ── Audience selector ── */}
<div>
  <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
    👥 Send To
  </label>
  <div className="flex items-center gap-3 flex-wrap">
    <div className="flex items-center gap-2">
      <button
        onClick={() => { setActiveYears(0); onAutoChange('PromoActiveYears', 0); }}
        className={`px-4 py-2 rounded-xl border text-xs font-semibold transition-all ${
          activeYears === 0
            ? 'bg-fuchsia-600 text-white border-fuchsia-600 shadow'
            : 'bg-white text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50'
        }`}
      >
        👥 All Active Clients
      </button>
      <span className="text-xs text-slate-400 font-medium">or</span>
      <span className="text-xs font-semibold text-slate-600">Past</span>
      <input
        type="number"
        min={1}
        max={20}
        value={activeYears === 0 ? '' : activeYears}
        placeholder="N"
        onChange={e => {
          const v = parseInt(e.target.value, 10) || 0;
          setActiveYears(v);
          onAutoChange('PromoActiveYears', v);
        }}
        className="w-16 px-2.5 py-2 border border-fuchsia-200 rounded-lg text-center text-sm font-bold text-fuchsia-700 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none"
      />
      <span className="text-xs font-semibold text-slate-600">year{activeYears !== 1 ? 's' : ''} active</span>
    </div>
  </div>
  {activeYears > 0 && (
    <p className="mt-2 text-xs text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
      🕐 Only clients with a transaction in the past <strong>{activeYears} year{activeYears !== 1 ? 's' : ''}</strong> will receive this campaign.
    </p>
  )}
</div>
          </div>

          {smsOn && (
            <div className="rounded-xl border border-fuchsia-100 overflow-hidden">
              <button
                onClick={() => setSmsCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-fuchsia-50/80 hover:bg-fuchsia-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">📱</span>
                  <span className="text-xs font-bold text-fuchsia-700 uppercase tracking-wide">SMS Bulk Campaign</span>
                </div>
                <span className="text-fuchsia-400 text-xs font-bold select-none">{smsCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>

              {!smsCollapsed && (
                <div className="bg-fuchsia-50/40 px-4 pb-4 pt-3 space-y-4">
                  <p className="text-xs text-slate-500 bg-white rounded-lg px-3 py-2 border border-fuchsia-200">
                    Sends to all active clients with a mobile number. Uses the <strong>Promotional SMS account</strong> if configured, otherwise falls back to the main account.
                  </p>

                  <Field label="Message" icon="✉️">
                    <Textarea
                      placeholder="Type your promotional SMS message here…"
                      value={smsMessage}
                      rows={4}
                      onChange={e => { setSmsMessage(e.target.value); setSendResult(null); }}
                      className="focus:border-fuchsia-400 focus:ring-fuchsia-100"
                    />
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-slate-400 text-xs">No placeholders — this message goes as-is to all clients.</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`font-semibold ${cc.chars > (cc.unicode ? 70 : 160) ? 'text-amber-600' : 'text-slate-400'}`}>
                          {cc.chars} chars
                        </span>
                        <span className={`px-2 py-0.5 rounded-full font-semibold border ${
                          cc.parts <= 1
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : cc.parts <= 3
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                          {cc.parts} SMS{cc.unicode ? ' (Unicode)' : ''}
                        </span>
                      </div>
                    </div>
                  </Field>

                  <div className="rounded-xl border border-fuchsia-200 overflow-hidden">
                    <div className="bg-fuchsia-50 px-4 py-2.5 flex items-center gap-2">
                      <span className="text-sm">🧪</span>
                      <span className="text-xs font-bold text-fuchsia-700 uppercase tracking-wide">Test Before Sending</span>
                    </div>
                    <div className="bg-fuchsia-50/40 px-4 pb-4 pt-3 space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          placeholder="Mobile number for test (digits only)"
                          value={smsTestMobile}
                          onChange={e => { setSmsTestMobile(e.target.value); setSmsTestResult(null); }}
                          className="flex-1 px-3.5 py-2.5 border border-fuchsia-200 rounded-lg focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100 outline-none text-slate-800 text-sm bg-white"
                        />
                        <button
                          onClick={sendSmsTest}
                          disabled={smsTestSending || !smsTestMobile || !smsMessage.trim()}
                          className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all whitespace-nowrap ${
                            smsTestSending || !smsTestMobile || !smsMessage.trim()
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow hover:shadow-md'
                          }`}
                        >
                          {smsTestSending
                            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
                            : <>📤 Send Test</>}
                        </button>
                      </div>
                      {smsTestResult && (
                        <div className={`rounded-lg border px-3 py-2.5 text-xs font-medium flex items-start gap-2 ${smsTestResult.ok ? 'bg-white border-emerald-200 text-emerald-800' : 'bg-white border-red-200 text-red-800'}`}>
                          <span>{smsTestResult.ok ? '✅' : '❌'}</span>
                          <div>
                            <div className="font-bold uppercase tracking-wide text-xs mb-0.5">{smsTestResult.ok ? 'Test sent' : 'Failed'}</div>
                            <div className="opacity-90 break-all">
                              {renderTestResult(smsTestResult, 'sms')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {waOn && (
            <div className="rounded-xl border border-emerald-100 overflow-hidden">
              <button
                onClick={() => setWaCollapsed(c => !c)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-50/80 hover:bg-emerald-100/60 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">💬</span>
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">WhatsApp Bulk Campaign</span>
                </div>
                <span className="text-emerald-400 text-xs font-bold select-none">{waCollapsed ? '▼ Show' : '▲ Hide'}</span>
              </button>

              {!waCollapsed && (
                <div className="bg-emerald-50/40 px-4 pb-4 pt-3 space-y-4">
                  
<p className="text-xs text-slate-500 bg-white rounded-lg px-3 py-2 border border-emerald-200">
  Sends to all active clients via an approved WhatsApp template. Works outside the 24h window — no session required. Configure the template ID in the <strong>WhatsApp</strong> tab under <em>Promotion Template ID</em>.
</p>

                  <Field label="Message" icon="✉️">
                    <Textarea
                      placeholder="Type your promotional WhatsApp message here…"
                      value={waMessage}
                      rows={4}
                      onChange={e => { setWaMessage(e.target.value); setSendResult(null); }}
                      className="focus:border-emerald-400 focus:ring-emerald-100"
                    />
                    <p className="text-slate-400 text-xs mt-1.5">Supports line breaks and basic formatting (*bold*, _italic_). No placeholders.</p>
                  </Field>

                  <div className="rounded-xl border border-emerald-200 overflow-hidden">
                    
<div className="bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
  <span className="text-sm">🧪</span>
  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Test Before Sending</span>
</div>
<div className="bg-emerald-50/40 px-4 pb-4 pt-3 space-y-3">
  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
    ⚠️ Bulk WA campaigns send via <strong>approved template</strong>. This test sends free-text to verify the message content and mobile number are correct — set your template ID in the <strong>WhatsApp</strong> tab before going live.
  </p>
  <div className="flex gap-2">
    <input
      type="tel"
      placeholder="Mobile with country code (e.g. 971501234567)"
      value={waTestMobile}
      onChange={e => { setWaTestMobile(e.target.value); setWaTestResult(null); }}
      className="flex-1 px-3.5 py-2.5 border border-emerald-200 rounded-lg focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-slate-800 text-sm bg-white"
    />
    <button
      onClick={sendWaTest}
      disabled={waTestSending || !waTestMobile || !waMessage.trim()}
      className={`px-5 py-2.5 font-semibold rounded-lg text-sm flex items-center gap-2 transition-all whitespace-nowrap ${
        waTestSending || !waTestMobile || !waMessage.trim()
          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
          : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow hover:shadow-md'
      }`}
    >
      {waTestSending
        ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
        : <>📤 Send Test</>}
    </button>
  </div>
  {waTestResult && (
    <div className={`rounded-lg border px-3 py-2.5 text-xs font-medium flex items-start gap-2 ${waTestResult.ok ? 'bg-white border-emerald-200 text-emerald-800' : 'bg-white border-red-200 text-red-800'}`}>
      <span>{waTestResult.ok ? '✅' : '❌'}</span>
      <div>
        <div className="font-bold uppercase tracking-wide text-xs mb-0.5">{waTestResult.ok ? 'Test sent' : 'Failed'}</div>
        <div className="opacity-90 break-all">
          {renderTestResult(waTestResult, 'wa')}
        </div>
      </div>
    </div>
  )}
</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(sending || progress) && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">📤 Sending campaign…</span>
                <button onClick={cancelSSE} className="text-xs text-slate-400 hover:text-slate-600 underline transition">Detach</button>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all duration-300 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {progress && (
                <div className="flex flex-wrap gap-3 text-xs">
                  {smsOn && <span className="bg-fuchsia-100 text-fuchsia-700 font-semibold px-2.5 py-1 rounded-full">📱 SMS: {progress.smsSent || 0}</span>}
                  {waOn  && <span className="bg-emerald-100 text-emerald-700 font-semibold px-2.5 py-1 rounded-full">💬 WA: {progress.waSent || 0}</span>}
                  <span className="bg-slate-100 text-slate-600 font-semibold px-2.5 py-1 rounded-full">⏭ Skipped: {progress.skipped || 0}</span>
                  {progress.errors > 0 && (
                    <span className="bg-red-100 text-red-700 font-semibold px-2.5 py-1 rounded-full">✗ Errors: {progress.errors}</span>
                  )}
                  <span className="bg-slate-100 text-slate-500 font-semibold px-2.5 py-1 rounded-full">Total: {progress.total}</span>
                  <span className="bg-fuchsia-100 text-fuchsia-700 font-semibold px-2.5 py-1 rounded-full">{pct}%</span>
                </div>
              )}
            </div>
          )}

          {sendResult && !sending && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🎉</span>
                <span className="font-semibold text-emerald-800">Campaign Complete</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                {smsOn && <span className="bg-fuchsia-100 text-fuchsia-700 font-semibold px-3 py-1.5 rounded-full">📱 SMS: {sendResult.smsSent || 0}</span>}
                {waOn  && <span className="bg-emerald-100 text-emerald-700 font-semibold px-3 py-1.5 rounded-full">💬 WA: {sendResult.waSent || 0}</span>}
                <span className="bg-slate-100 text-slate-600 font-semibold px-3 py-1.5 rounded-full">⏭ {sendResult.skipped || 0} skipped</span>
                {sendResult.errors > 0 && (
                  <span className="bg-red-100 text-red-700 font-semibold px-3 py-1.5 rounded-full">✗ {sendResult.errors} errors</span>
                )}
                <span className="bg-slate-100 text-slate-500 font-semibold px-3 py-1.5 rounded-full">of {sendResult.total} clients</span>
              </div>
            </div>
          )}

          {jobError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <span>⚠️</span><span>{jobError}</span>
            </div>
          )}

          <button
            onClick={startSend}
            disabled={!canSend}
            className={`w-full py-3 font-bold rounded-xl text-sm flex items-center justify-center gap-2.5 transition-all shadow ${
              !canSend
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white hover:shadow-lg hover:scale-[1.01] active:scale-100'
            }`}
          >
            {sending
              ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending to all clients…</>
             : <>📣 Send Campaign to {activeYears > 0 ? `Last ${activeYears} Year${activeYears !== 1 ? 's' : ''} Active Clients` : 'All Active Clients'}</>}
          </button>
        </div>
      )}

      {!anyOn && (
        <div className="px-4 py-3 text-xs text-slate-400">
          Enable SMS, WhatsApp — or both — to compose and send a bulk promotional campaign to all active clients.
        </div>
      )}
    </div>
  );
}

function AutoTab({ data, onChange, onSave, saving, showToast, waSettings }) {
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <div className="font-semibold text-slate-700 text-sm">Independent channel control per module</div>
            <p className="text-xs text-slate-400 mt-0.5">
              Toggle <strong>📱 SMS</strong> and <strong>💬 WA</strong> on each card independently. Each channel section can be collapsed to save space.
            </p>
          </div>
        </div>
      </div>
      <Section icon="🏢" title="General" accent="slate">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Site Name" icon="🏷️">
            <Input
              placeholder="My Spa"
              value={data.SiteName ?? ''}
              onChange={e => onChange('SiteName', e.target.value)}
            />
          </Field>
          <Field label="Phone Digit Count" icon="📱" hint="Digits to keep from right — e.g. 9">
            <Input
              type="number"
              min={7}
              max={15}
              placeholder="9"
              value={data.PhoneDigit ?? 9}
              onChange={e => onChange('PhoneDigit', parseInt(e.target.value, 10))}
              className="text-center font-semibold"
            />
          </Field>
        </div>
      </Section>
       <div className="space-y-4">
  {MODULE_CONFIGS.filter(cfg => cfg.key === 'reminder').map(cfg => (
    <ModuleCard key={cfg.key} cfg={cfg} data={data} onChange={onChange} waSettings={waSettings} />
  ))}
  <PreArrivalCard data={data} onChange={onChange} waSettings={waSettings} />
  {MODULE_CONFIGS.filter(cfg => cfg.key !== 'reminder' && !cfg.renderedSeparately).map(cfg => (
    <ModuleCard key={cfg.key} cfg={cfg} data={data} onChange={onChange} waSettings={waSettings} />
  ))}
  <PromotionsCard showToast={showToast} autoSettings={data} onAutoChange={onChange} />
</div>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

// ─── Save Bar ─────────────────────────────────────────────────────────────────

function SaveBar({ onSave, saving }) {
  return (
    <div className="sticky bottom-4 z-40">
      <div className="bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl shadow-xl px-5 py-3.5 flex items-center justify-between">
        <span className="text-xs text-slate-400">Changes take effect within 60 seconds of saving.</span>
        <button
          onClick={onSave}
          disabled={saving}
          className={`px-7 py-2.5 font-semibold rounded-xl shadow flex items-center gap-2 transition-all duration-200 text-sm ${
            saving
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:scale-[1.02] active:scale-100'
          }`}
        >
          {saving
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving…</>
            : <>💾 Save Settings</>}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MessagingSettings() {
  const [activeTab, setActiveTab] = useState('auto');
  const [settings, setSettings]   = useState({ sms: {}, wa: {}, auto: {} });
  const [loaded, setLoaded]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    api.get('/messaging/settings')
      .then(r => { setSettings(r.data || {}); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleChange = useCallback((section, key, value) => {
    setSettings(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  }, []);

  const handleSave = useCallback(async (section) => {
    setSaving(true);
    try {
      await api.put(`/messaging/settings/${section}`, settings[section]);
      showToast('Settings saved!', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }, [settings, showToast]);

  const smsCount = MODULE_CONFIGS.filter(m => settings.auto?.[m.smsActiveFlag]).length;
  const waCount  = MODULE_CONFIGS.filter(m => settings.auto?.[m.waActiveFlag]).length;

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-cyan-200 border-t-cyan-600 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm font-medium">Loading messaging settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-12">
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
  <div className="flex items-start justify-between flex-wrap gap-4">
    <div>
      <div className="flex items-center gap-3 mb-1">
        <span className="text-2xl">💬</span>
        <h1 className="text-xl font-bold tracking-tight">Messaging Settings</h1>
      </div>
      <p className="text-cyan-100 text-sm">Configure SMS and WhatsApp automation for client communications</p>
    </div>
    <div className="flex gap-3">
      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/30 min-w-[80px]">
        <div className="text-xs text-cyan-200 uppercase tracking-wide mb-0.5">📱 SMS</div>
        <div className="text-2xl font-bold">{smsCount}</div>
        <div className="text-xs text-cyan-200">modules</div>
      </div>
      <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 text-center border border-white/30 min-w-[80px]">
        <div className="text-xs text-cyan-200 uppercase tracking-wide mb-0.5">💬 WA</div>
        <div className="text-2xl font-bold">{waCount}</div>
        <div className="text-xs text-cyan-200">modules</div>
      </div>
    </div>
  </div>

  {/* ── Today's stats ── */}
<div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-3">
  <StatsCards />
</div>
</div>

      <div className="bg-white rounded-xl border border-slate-200 p-1.5 flex gap-1 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap transition-all duration-200 flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}
          >
            <span>{tab.icon}</span>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'sms'    && <SmsTab data={settings.sms} onChange={(k, v) => handleChange('sms', k, v)} onSave={() => handleSave('sms')} saving={saving} />}
      {activeTab === 'wa'     && <WaTab  data={settings.wa}  onChange={(k, v) => handleChange('wa', k, v)}  onSave={() => handleSave('wa')}  saving={saving} />}
      {activeTab === 'auto'   && <AutoTab data={settings.auto} onChange={(k, v) => handleChange('auto', k, v)} onSave={() => handleSave('auto')} saving={saving} showToast={showToast} waSettings={settings.wa} />}
      {activeTab === 'logs'   && <LogsTab />}
      {activeTab === 'manual' && <ManualTab showToast={showToast} />}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.25s ease-out; }
      `}</style>
    </div>
  );
}